import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, apiJson, readApiError } from './api';
import Icon from './Icons';
import ErrorScreen from './ErrorScreen';
import { useConnectivity } from './hooks';
import AutocompleteInput from './AutocompleteInput';
import Cropper from 'react-easy-crop';
import { formatDateWithFmt, getStoredDateFmt } from './DateFormatContext';
import QRCodeSheet from './QRCodeSheet';
import { copyToClipboard } from './clipboard';

// ─── Constants ─────────────────────────────────────────────────────────────

const PHOTO_PART_OPTIONS = [
  { value: '', label: 'Unlabeled' },
  { value: 'full_plant', label: 'Full plant' },
  { value: 'leaf', label: 'Leaf' },
  { value: 'petiole', label: 'Petiole' },
  { value: 'stem_node', label: 'Stem / node' },
  { value: 'roots', label: 'Roots' },
  { value: 'flower', label: 'Flower' },
  { value: 'fruit', label: 'Fruit' },
  { value: 'habit', label: 'Growth habit' },
  { value: 'other', label: 'Other' },
];

const PHOTO_GROWTH_STAGE_OPTIONS = [
  { value: '', label: 'Unspecified' },
  { value: 'new_growth', label: 'New growth' },
  { value: 'juvenile', label: 'Juvenile' },
  { value: 'transitional', label: 'Transitional' },
  { value: 'mature', label: 'Mature' },
];

const PHOTO_GROWTH_STAGE_LABELS = Object.fromEntries(PHOTO_GROWTH_STAGE_OPTIONS.map(option => [option.value, option.label]));

function photoGrowthStageLabel(value) {
  return PHOTO_GROWTH_STAGE_LABELS[value] || '';
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatRelativeTime(str) {
  if (!str) return 'Never';
  const d = new Date(str.includes('T') ? str : str.replace(' ', 'T') + 'Z');
  const diff = Date.now() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 2) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 14) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatFutureTime(str) {
  if (!str) return '—';
  const d = new Date(str.includes('T') ? str : str.replace(' ', 'T') + 'Z');
  const diff = d.getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `in ${hours}h`;
  const days = Math.floor(hours / 24);
  return `in ${days}d`;
}

function formatDate(str) {
  return formatDateWithFmt(str, getStoredDateFmt());
}

function inviteUrlForCode(code) {
  if (!code) return '';
  return `${window.location.origin}/signup?code=${encodeURIComponent(code)}`;
}


// ─── Stat Card ─────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent = false, warn = false, onClick }) {
  const color = warn ? 'var(--danger)' : accent ? 'var(--green)' : 'var(--text)';
  const border = warn ? 'var(--danger)' : accent ? 'var(--green)' : 'var(--border)';
  const interactive = !!onClick;
  return (
    <div
      className="stat-card"
      style={{ borderLeftColor: border, cursor: interactive ? 'pointer' : undefined }}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e => { if (e.key === 'Enter' || e.key === ' ') onClick(); }) : undefined}
    >
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value" style={{ color }}>{value ?? '—'}</div>
      {sub && <div className="stat-card-sub">{sub}</div>}
    </div>
  );
}

// ─── Section Card ──────────────────────────────────────────────────────────

function SectionCard({ title, children, action }) {
  return (
    <div className="stats-section-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <h2 className="stats-section-title" style={{ margin: 0 }}>{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function AdminWorkspacesSection() {
  const [traitReviewCount, setTraitReviewCount] = useState(null);

  useEffect(() => {
    apiJson('/operator/trait-review-queue')
      .then(d => setTraitReviewCount(d.total))
      .catch(() => setTraitReviewCount(null));
  }, []);

  const workspaceLinkStyle = {
    display: 'block',
    padding: '0.8rem 0.9rem',
    border: '1px solid var(--border)',
    borderRadius: 8,
    background: 'var(--surface)',
    color: 'inherit',
    textDecoration: 'none',
  };

  return (
    <SectionCard title="Admin Workspaces">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.65rem' }}>
        <Link to="/admin/care-profiles" style={workspaceLinkStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.25rem', color: 'var(--text)' }}>
            <Icon name="leaf" size={16} />
            <span style={{ fontSize: '0.9rem', fontWeight: 700 }}>Care Profiles</span>
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
            Review profiles, search statuses, manage generation jobs, and run the batch pipeline.
          </div>
        </Link>
        <Link to="/admin/trait-review" style={{ ...workspaceLinkStyle, borderColor: traitReviewCount ? 'var(--warning-border, #fcd34d)' : 'var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.25rem', color: 'var(--text)' }}>
            <Icon name="leaf" size={16} />
            <span style={{ fontSize: '0.9rem', fontWeight: 700 }}>Trait Review</span>
            {traitReviewCount != null && traitReviewCount > 0 && (
              <span style={{ fontSize: '0.72rem', fontWeight: 700, background: 'var(--warning, #d97706)', color: '#fff', borderRadius: 10, padding: '0.1rem 0.45rem', lineHeight: 1.4 }}>
                {traitReviewCount}
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
            Resolve AI-generated trait flags one profile at a time with photos and image search on hand.
          </div>
        </Link>
      </div>
    </SectionCard>
  );
}

// ─── Collapsible Accordion ─────────────────────────────────────────────────

function Accordion({ title, defaultOpen = false, children, badge, onFirstOpen, forceOpen, id }) {
  const [open, setOpen] = useState(defaultOpen);
  const hasOpenedRef = useRef(defaultOpen);

  useEffect(() => {
    if (defaultOpen && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      setOpen(true);
    }
  }, [defaultOpen]);

  useEffect(() => {
    if (forceOpen > 0) {
      hasOpenedRef.current = true;
      setOpen(true);
    }
  }, [forceOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggle() {
    const next = !open;
    if (next && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      onFirstOpen?.();
    }
    setOpen(next);
  }

  return (
    <div className="fo-section" id={id}>
      <button
        type="button"
        className="fo-section-header"
        onClick={toggle}
      >
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={16} className="fo-section-chevron" />
        <span className="fo-section-title">{title}</span>
        {badge != null && (
          <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500 }}>
            {badge}
          </span>
        )}
      </button>
      {open && <div className="fo-section-body">{children}</div>}
    </div>
  );
}

// ─── Inline Confirm Button ─────────────────────────────────────────────────

function ConfirmButton({ label, confirmLabel = 'Confirm', onConfirm, busy, className = 'settings-lookup-action', danger = true }) {
  const [confirming, setConfirming] = useState(false);
  if (confirming) {
    return (
      <div className="inline-confirm" style={{ display: 'inline-flex', gap: '0.4rem', alignItems: 'center' }}>
        <span className="inline-confirm-msg" style={{ fontSize: '0.8rem' }}>{confirmLabel}?</span>
        <div className="inline-confirm-actions">
          <button type="button" className="inline-confirm-no" onClick={() => setConfirming(false)}>Cancel</button>
          <button
            type="button"
            className="inline-confirm-yes"
            disabled={busy}
            onClick={() => { setConfirming(false); onConfirm(); }}
          >
            {busy ? '…' : 'Yes'}
          </button>
        </div>
      </div>
    );
  }
  return (
    <button
      type="button"
      className={className}
      style={danger ? { color: 'var(--danger)' } : {}}
      disabled={busy}
      onClick={() => setConfirming(true)}
    >
      {label}
    </button>
  );
}

// ─── Copy to Clipboard Button ──────────────────────────────────────────────

function CopyButton({ text, label = 'Copy Link' }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    if (await copyToClipboard(text)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button type="button" className="settings-lookup-action" onClick={handleCopy}>
      {copied ? 'Copied!' : label}
    </button>
  );
}

// ─── Overview Section ──────────────────────────────────────────────────────

function OverviewSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await apiJson('/operator/overview');
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshBtn = (
    <button type="button" className="settings-lookup-action" onClick={() => load()} disabled={loading}>
      {loading ? 'Loading…' : 'Refresh'}
    </button>
  );

  return (
    <SectionCard title="Overview" action={refreshBtn}>
      {error && <p className="settings-save-error">{error}</p>}
      {loading && !data && <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</p>}
      {data && (
        <>
          <div className="stat-cards-grid">
            <StatCard label="Users" value={data.user_count} accent />
            <StatCard label="Households" value={data.household_count} />
            <StatCard label="Plants" value={data.plant_count} sub="active collection" />
            <StatCard label="Observations" value={data.observation_count} />
            <StatCard label="Photos" value={data.photo_count} />
            <StatCard
              label="Active Sessions"
              value={data.active_session_count}
              onClick={data.active_session_count > 0 ? () => {
                document.getElementById('admin-sessions')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                document.dispatchEvent(new CustomEvent('admin:open-sessions'));
              } : undefined}
            />
            <StatCard
              label="Locked Accounts"
              value={data.locked_account_count}
              warn={data.locked_account_count > 0}
              onClick={data.locked_account_count > 0 ? () => {
                document.getElementById('admin-users')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                document.dispatchEvent(new CustomEvent('admin:filter-users', { detail: { status: 'locked' } }));
              } : undefined}
            />
            <StatCard label="DB Size" value={formatBytes(data.db_size_bytes)} />
            <StatCard label="Photo Storage" value={formatBytes(data.photo_dir_size_bytes)} />
          </div>
          {data.last_migration && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.75rem', marginBottom: 0 }}>
              Last migration: <strong>{data.last_migration.filename}</strong> &mdash; {formatRelativeTime(data.last_migration.applied_at)}
            </p>
          )}
        </>
      )}
    </SectionCard>
  );
}

// ─── Users Section ─────────────────────────────────────────────────────────

const STATUS_PILL = {
  base: { fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', borderRadius: '4px', padding: '2px 6px' },
  superadmin: { color: 'var(--purple)', background: 'var(--surface-recessed)' },
  disabled:   { color: 'var(--danger)', background: 'var(--surface-recessed)' },
  locked:     { color: 'var(--orange, #e07b3c)', background: 'var(--surface-recessed)' },
  unverified: { color: 'var(--orange, #e07b3c)' },
};

function StatusPill({ type, label }) {
  return (
    <span style={{ ...STATUS_PILL.base, ...STATUS_PILL[type] }}>{label}</span>
  );
}

function UserRow({ user, onAction }) {
  const [resetUrl, setResetUrl] = useState(null);
  const [busy, setBusy] = useState({});
  const [error, setError] = useState(null);
  const [resendStatus, setResendStatus] = useState(null); // { ok, message } | null

  const act = useCallback(async (key, fn) => {
    setBusy(b => ({ ...b, [key]: true }));
    setError(null);
    try {
      await fn();
      onAction();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  }, [onAction]);

  const handleResetLink = async () => {
    setBusy(b => ({ ...b, reset: true }));
    setError(null);
    try {
      const d = await apiJson(`/operator/users/${user.id}/password-reset-link`, { method: 'POST' });
      setResetUrl(d.reset_url);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(b => ({ ...b, reset: false }));
    }
  };

  const handleResendVerification = async () => {
    setBusy(b => ({ ...b, resend: true }));
    setResendStatus(null);
    try {
      const d = await apiJson(`/operator/users/${user.id}/resend-verification`, { method: 'POST' });
      setResendStatus({ ok: d.ok, message: d.ok ? `Verification email sent to ${d.email}` : (d.reason || 'Could not send verification email') });
      if (d.ok) setTimeout(() => setResendStatus(null), 4000);
    } catch (e) {
      setResendStatus({ ok: false, message: e.message });
    } finally {
      setBusy(b => ({ ...b, resend: false }));
    }
  };

  const isLocked = !!user.is_locked;
  const hasSessions = user.active_session_count > 0;

  return (
    <div style={{ padding: '0.7rem 0', borderBottom: '1px solid var(--border)' }}>
      {/* Name row + status pills */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.2rem' }}>
        <span style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text)' }}>
          {user.display_name || user.email}
        </span>
        {user.display_name && (
          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{user.email}</span>
        )}
        {user.is_superadmin ? <StatusPill type="superadmin" label="superadmin" /> : null}
        {user.disabled_at ? <StatusPill type="disabled" label="disabled" /> : null}
        {isLocked ? <StatusPill type="locked" label="locked" /> : null}
        {!user.email_verified_at ? <StatusPill type="unverified" label="unverified" /> : null}
      </div>

      {/* Single compact meta line */}
      <p style={{ margin: '0 0 0.45rem', fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
        <button
          type="button"
          onClick={() => {
            document.getElementById('admin-households')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            document.dispatchEvent(new CustomEvent('admin:filter-households', { detail: { q: user.email } }));
          }}
          style={{ color: 'var(--green)', cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit', background: 'none', border: 'none', padding: 0 }}
        >
          {user.household_count} household{user.household_count !== 1 ? 's' : ''}
        </button>
        {' · '}
        {user.active_session_count} session{user.active_session_count !== 1 ? 's' : ''}
        {' · '}
        {`Joined ${formatDate(user.created_at)}`}
        {' · '}
        {user.last_login_at ? `Last login ${formatRelativeTime(user.last_login_at)}` : 'Never logged in'}
        {user.total_failed_attempts > 0 && ` · ${user.total_failed_attempts} failed attempt${user.total_failed_attempts !== 1 ? 's' : ''}`}
      </p>

      {/* Actions row */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className="settings-lookup-action" onClick={handleResetLink} disabled={busy.reset}>
          {busy.reset ? '…' : 'Reset Link'}
        </button>
        {!user.email_verified_at && !user.disabled_at && (
          <button type="button" className="settings-lookup-action" onClick={handleResendVerification} disabled={busy.resend}>
            {busy.resend ? '…' : 'Resend verification'}
          </button>
        )}

        {isLocked && (
          <ConfirmButton
            label="Clear Lockout"
            confirmLabel="Clear login lockout"
            busy={busy.lockout}
            onConfirm={() => act('lockout', () =>
              apiFetch(`/operator/users/${user.id}/lockout`, { method: 'DELETE' })
                .then(r => { if (!r.ok) throw new Error('Failed'); })
            )}
          />
        )}

        {hasSessions && (
          <ConfirmButton
            label="Revoke Sessions"
            confirmLabel="Revoke all sessions"
            busy={busy.sessions}
            onConfirm={() => act('sessions', () =>
              apiFetch(`/operator/users/${user.id}/sessions`, { method: 'DELETE' })
                .then(r => { if (!r.ok) throw new Error('Failed'); })
            )}
          />
        )}

        {!user.is_superadmin && (user.disabled_at ? (
          <ConfirmButton
            label="Enable"
            confirmLabel="Re-enable this account"
            busy={busy.enable}
            danger={false}
            onConfirm={() => act('enable', () => apiJson(`/operator/users/${user.id}/enable`, { method: 'PUT' }))}
          />
        ) : (
          <ConfirmButton
            label="Disable"
            confirmLabel="Disable and log out this user"
            busy={busy.disable}
            onConfirm={() => act('disable', () => apiJson(`/operator/users/${user.id}/disable`, { method: 'PUT' }))}
          />
        ))}
      </div>

      {/* Reset URL box */}
      {resetUrl && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--surface-recessed)', borderRadius: '6px', padding: '0.45rem 0.65rem', marginTop: '0.45rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resetUrl}</span>
          <CopyButton text={resetUrl} label="Copy" />
          <button type="button" className="settings-lookup-action" onClick={() => setResetUrl(null)} style={{ padding: '0 0.25rem' }} aria-label="Dismiss" title="Dismiss">✕</button>
        </div>
      )}

      {resendStatus && (
        <p className={resendStatus.ok ? 'settings-save-status' : 'settings-save-error'} style={{ margin: '0.3rem 0 0' }}>
          {resendStatus.message}
        </p>
      )}
      {error && <p className="settings-save-error" style={{ margin: '0.3rem 0 0' }}>{error}</p>}
    </div>
  );
}

const SORT_OPTIONS = [
  { value: 'created_at:desc',   label: 'Newest signup' },
  { value: 'created_at:asc',    label: 'Oldest signup' },
  { value: 'last_login_at:desc', label: 'Latest login' },
  { value: 'last_login_at:asc',  label: 'Earliest login' },
];

const USER_STATUS_CHIPS = [
  { key: '', label: 'All' },
  { key: 'locked', label: 'Locked' },
  { key: 'unverified', label: 'Unverified' },
  { key: 'disabled', label: 'Disabled' },
];

function UsersSection() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('created_at:desc');
  const [statusFilter, setStatusFilter] = useState('');
  const [openNonce, setOpenNonce] = useState(0);
  const debounceRef = useRef(null);

  const load = useCallback(async (query = '', sortVal = 'created_at:desc', status = '', off = 0, append = false) => {
    setLoading(true);
    setError(null);
    try {
      const [sortBy, sortDir] = sortVal.split(':');
      const params = new URLSearchParams({ sort_by: sortBy, sort_dir: sortDir, limit: '50', offset: String(off) });
      if (query) params.set('q', query);
      if (status) params.set('status', status);
      const d = await apiJson(`/operator/users?${params}`);
      setUsers(prev => append ? [...prev, ...(d.users || [])] : (d.users || []));
      setTotal(d.total ?? null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const status = e.detail?.status || '';
      setStatusFilter(status);
      load(q, sort, status, 0);
      setOpenNonce(n => n + 1);
    };
    document.addEventListener('admin:filter-users', handler);
    return () => document.removeEventListener('admin:filter-users', handler);
  }, [q, sort, load]);

  const handleSearch = (val) => {
    setQ(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(val, sort, statusFilter, 0), 350);
  };

  const handleSort = (val) => {
    setSort(val);
    load(q, val, statusFilter, 0);
  };

  const handleStatusChip = (status) => {
    setStatusFilter(status);
    load(q, sort, status, 0);
  };

  const loadMore = () => {
    load(q, sort, statusFilter, users.length, true);
  };

  const chipStyle = (active) => ({
    padding: '0.25rem 0.6rem',
    borderRadius: 20,
    border: '1px solid',
    borderColor: active ? 'var(--green)' : 'var(--border)',
    background: active ? 'var(--green)' : 'transparent',
    color: active ? '#fff' : 'var(--text-muted)',
    fontSize: '0.78rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  });

  const badge = total !== null ? total : null;

  return (
    <Accordion
      id="admin-users"
      title="Users"
      defaultOpen={false}
      badge={badge}
      onFirstOpen={() => load(q, sort, statusFilter, 0)}
      forceOpen={openNonce}
    >
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <input
          className="form-input"
          placeholder="Search by email or name…"
          value={q}
          onChange={e => handleSearch(e.target.value)}
          style={{ flex: 1, minWidth: '160px' }}
        />
        <select
          className="form-input"
          value={sort}
          onChange={e => handleSort(e.target.value)}
          style={{ fontSize: '0.82rem', padding: '0.3rem 0.5rem', flexShrink: 0 }}
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '0.65rem', flexWrap: 'wrap' }}>
        {USER_STATUS_CHIPS.map(c => (
          <button key={c.key} type="button" style={chipStyle(statusFilter === c.key)} onClick={() => handleStatusChip(c.key)}>
            {c.label}
          </button>
        ))}
      </div>
      {error && <p className="settings-save-error">{error}</p>}
      {loading && users.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</p>}
      {users.length > 0 && (
        <>
          <p style={{ fontSize: '0.77rem', color: 'var(--text-muted)', margin: '0 0 0.4rem' }}>
            Showing {users.length} of {total ?? '…'} user{total !== 1 ? 's' : ''}
            {loading ? ' · refreshing…' : ''}
          </p>
          {users.map(u => (
            <UserRow key={u.id} user={u} onAction={() => load(q, sort, statusFilter, 0)} />
          ))}
          {total !== null && users.length < total && (
            <button type="button" className="settings-lookup-action" onClick={loadMore} disabled={loading} style={{ marginTop: '0.5rem' }}>
              {loading ? '…' : 'Load more'}
            </button>
          )}
        </>
      )}
      {!loading && users.length === 0 && <p className="settings-empty-note">No users found.</p>}
    </Accordion>
  );
}

// ─── Login Attempts Section ────────────────────────────────────────────────

function LoginAttemptsSection() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiJson('/operator/login-attempts');
      setRows(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clearLockout = async (userId, email) => {
    const key = `lock:${email}`;
    setBusy(b => ({ ...b, [key]: true }));
    setError(null);
    try {
      const res = await apiFetch(`/operator/users/${userId}/lockout`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await readApiError(res));
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  };

  const activeLockouts = (rows || []).filter(r => r.is_active_lockout);
  const recentAttempts = (rows || []).filter(r => !r.is_active_lockout);

  const badge = rows
    ? (activeLockouts.length > 0 ? `${activeLockouts.length} locked` : rows.length > 0 ? `${rows.length} records` : null)
    : null;

  return (
    <Accordion title="Failed Logins" defaultOpen={activeLockouts.length > 0} badge={badge}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
        <button type="button" className="settings-lookup-action" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {error && <p className="settings-save-error">{error}</p>}

      {rows && rows.length === 0 && (
        <p className="settings-empty-note">No failed login records.</p>
      )}

      {activeLockouts.length > 0 && (
        <>
          <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--danger)', margin: '0 0 0.4rem' }}>
            Active Lockouts
          </p>
          {activeLockouts.map(r => (
            <div key={`${r.email}:${r.ip_address}`} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border)', gap: '0.5rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.84rem' }}>{r.display_name || r.email}</div>
                {r.display_name && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{r.email}</div>}
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                  {r.failed_count} failure{r.failed_count !== 1 ? 's' : ''} from {r.ip_address}
                  {' · '}Locked until {formatRelativeTime(r.locked_until)}
                  {' · '}Last attempt {formatRelativeTime(r.last_failed_at)}
                </div>
              </div>
              {r.user_id && (
                <ConfirmButton
                  label="Clear"
                  confirmLabel="Clear this lockout"
                  busy={busy[`lock:${r.email}`]}
                  danger={false}
                  onConfirm={() => clearLockout(r.user_id, r.email)}
                />
              )}
            </div>
          ))}
        </>
      )}

      {recentAttempts.length > 0 && (
        <>
          <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', margin: `${activeLockouts.length > 0 ? '0.75rem' : '0'} 0 0.4rem` }}>
            Recent Failed Attempts
          </p>
          {recentAttempts.map(r => (
            <div key={`${r.email}:${r.ip_address}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.8rem', gap: '0.5rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 500 }}>{r.display_name || r.email}</span>
                <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                  {r.failed_count} failure{r.failed_count !== 1 ? 's' : ''} · {r.ip_address} · {formatRelativeTime(r.last_failed_at)}
                </span>
              </div>
            </div>
          ))}
        </>
      )}
    </Accordion>
  );
}

// ─── Signup Mode Section ───────────────────────────────────────────────────

function SignupModeSection() {
  const [signupMode, setSignupMode] = useState(null);
  const [signupModeLoading, setSignupModeLoading] = useState(true);
  const [signupModeBusy, setSignupModeBusy] = useState(false);
  const [signupModeError, setSignupModeError] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    setSignupModeLoading(true);
    setSignupModeError(null);
    fetch('/auth/config')
      .then(r => {
        if (!r.ok) throw new Error('Could not load signup mode');
        return r.json();
      })
      .then(data => {
        if (data?.signup_mode) {
          setSignupMode(data.signup_mode);
        } else {
          throw new Error('Could not load signup mode');
        }
      })
      .catch(e => setSignupModeError(e.message))
      .finally(() => setSignupModeLoading(false));
  }, []);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  function flashToast(msg) {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(msg);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 3000);
  }

  async function handleSignupModeChange(mode) {
    if (signupModeLoading || signupModeBusy || !signupMode || mode === signupMode) return;
    const prev = signupMode;
    setSignupMode(mode);
    setSignupModeBusy(true);
    setSignupModeError(null);
    try {
      const res = await apiFetch('/api/settings/signup_mode', {
        method: 'PUT',
        body: JSON.stringify({ value: mode }),
      });
      if (!res.ok) throw new Error(await readApiError(res));
      flashToast(`Signup mode changed to: ${mode === 'open' ? 'Open' : 'Closed'}`);
    } catch (e) {
      setSignupMode(prev);
      setSignupModeError(e.message);
    } finally {
      setSignupModeBusy(false);
    }
  }

  return (
    <SectionCard title="Platform">
      {toast && <div className="toast" onClick={() => setToast(null)}>{toast}</div>}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Signup Mode</span>
        <div className="settings-segment admin-signup-mode-toggle">
          <button
            type="button"
            className={`settings-segment-btn ${signupMode === 'closed' ? 'settings-segment-btn--active' : ''}`}
            onClick={() => handleSignupModeChange('closed')}
            disabled={signupModeLoading || signupModeBusy || !signupMode}
          >
            Closed
          </button>
          <button
            type="button"
            className={`settings-segment-btn ${signupMode === 'open' ? 'settings-segment-btn--active' : ''}`}
            onClick={() => handleSignupModeChange('open')}
            disabled={signupModeLoading || signupModeBusy || !signupMode}
          >
            Open
          </button>
        </div>
      </div>
      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0.4rem 0 0' }}>
        Closed: new accounts require an invite code. Open: anyone can create a new household at signup.
      </p>
      {signupModeLoading && <p className="settings-save-status">Loading signup mode…</p>}
      {signupModeError && <p className="settings-save-error">{signupModeError}</p>}
    </SectionCard>
  );
}

// ─── Integrations Section ─────────────────────────────────────────────────

function IntegrationsSection() {
  const [idStatus, setIdStatus] = useState(null);
  const [quota, setQuota] = useState(null);
  const [plantidEnabled, setPlantidEnabled] = useState(null);
  const [idProvider, setIdProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  function flashToast(msg) {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(msg);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 3000);
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusData, quotaData] = await Promise.all([
        apiJson('/operator/id-status'),
        apiJson('/id-quota'),
      ]);
      setIdStatus(statusData);
      setQuota(quotaData);
      setPlantidEnabled(statusData.plantid_enabled);
      setIdProvider(statusData.id_provider || 'plantnet');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  async function handleTogglePlantidEnabled(val) {
    if (busy) return;
    const prev = plantidEnabled;
    setPlantidEnabled(val);
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/settings/plantid_enabled', {
        method: 'PUT',
        body: JSON.stringify({ value: val ? 'true' : 'false' }),
      });
      if (!res.ok) throw new Error(await readApiError(res));
      flashToast(`Plant.id ${val ? 'enabled' : 'disabled'}`);
    } catch (e) {
      setPlantidEnabled(prev);
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleProviderChange(val) {
    if (busy || val === idProvider) return;
    const prev = idProvider;
    setIdProvider(val);
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/settings/id_provider', {
        method: 'PUT',
        body: JSON.stringify({ value: val }),
      });
      if (!res.ok) throw new Error(await readApiError(res));
      flashToast(`Provider changed to ${val === 'plantid' ? 'Better match (Plant.id)' : 'Standard (PlantNet)'}`);
    } catch (e) {
      setIdProvider(prev);
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const providerDisabled = busy || !plantidEnabled || !idStatus?.plantid_key_configured;

  return (
    <Accordion id="admin-integrations" title="Integrations" onFirstOpen={loadData}>
      {toast && <div className="toast" onClick={() => setToast(null)}>{toast}</div>}
      {loading && !idStatus && <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</p>}
      {error && <p className="settings-save-error">{error}</p>}

      {idStatus !== null && (
        <>
          {/* Plant.id API key status */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.5rem 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Plant.id API key</span>
            {idStatus.plantid_key_configured
              ? <span style={{ fontSize: '0.8rem', color: 'var(--success, #2e7d32)', fontWeight: 500 }}>Configured via environment</span>
              : <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Not configured — set PLANTID_API_KEY on the server</span>
            }
          </div>

          {/* Plant.id enabled toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.6rem 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Plant.id enabled</span>
            <div className="settings-segment admin-plantid-enabled-toggle">
              <button
                type="button"
                className={`settings-segment-btn ${plantidEnabled ? '' : 'settings-segment-btn--active'}`}
                onClick={() => handleTogglePlantidEnabled(false)}
                disabled={busy}
              >
                Off
              </button>
              <button
                type="button"
                className={`settings-segment-btn ${plantidEnabled ? 'settings-segment-btn--active' : ''}`}
                onClick={() => handleTogglePlantidEnabled(true)}
                disabled={busy}
              >
                On
              </button>
            </div>
          </div>

          {/* ID provider selector */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.6rem 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
            <div>
              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Identification provider</span>
              {providerDisabled && (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.2rem 0 0' }}>
                  {!idStatus.plantid_key_configured ? 'Requires PLANTID_API_KEY' : 'Enable Plant.id to switch provider'}
                </p>
              )}
            </div>
            <div className="settings-segment">
              <button
                type="button"
                className={`settings-segment-btn ${idProvider === 'plantnet' ? 'settings-segment-btn--active' : ''}`}
                onClick={() => handleProviderChange('plantnet')}
                disabled={providerDisabled}
              >
                Standard
              </button>
              <button
                type="button"
                className={`settings-segment-btn ${idProvider === 'plantid' ? 'settings-segment-btn--active' : ''}`}
                onClick={() => handleProviderChange('plantid')}
                disabled={providerDisabled}
              >
                Better match
              </button>
            </div>
          </div>

          {/* Quota display */}
          <div style={{ padding: '0.6rem 0' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Plant.id quota</span>
            {!plantidEnabled ? (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.3rem 0 0' }}>Plant.id not active</p>
            ) : quota ? (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.3rem 0 0' }}>
                {quota.calls_today} / {quota.daily_cap} calls used today — resets at midnight UTC
              </p>
            ) : (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.3rem 0 0' }}>Loading quota…</p>
            )}
          </div>
        </>
      )}
    </Accordion>
  );
}

// ─── Households Section ────────────────────────────────────────────────────

function MilestoneChip({ done, label }) {
  return (
    <span className={`admin-milestone-chip ${done ? 'admin-milestone-chip--done' : 'admin-milestone-chip--todo'}`}>
      {done ? '✓' : '○'} {label}
    </span>
  );
}

function HouseholdNotes({ householdId, initialNotes }) {
  const [notes, setNotes] = useState(initialNotes || '');
  const [draftNotes, setDraftNotes] = useState(initialNotes || '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  function startEditing() {
    setDraftNotes(notes);
    setError(null);
    setEditing(true);
  }

  function cancelEditing() {
    setDraftNotes(notes);
    setError(null);
    setEditing(false);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/operator/households/${householdId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operator_notes: draftNotes }),
      });
      if (!res.ok) throw new Error(await readApiError(res));
      setNotes(draftNotes);
      setSaved(true);
      setEditing(false);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div className="admin-household-notes-row">
        {notes
          ? <span className="admin-household-notes-text">{notes}</span>
          : <span className="admin-household-notes-empty">Add operator note…</span>}
        {saved && <span className="admin-household-notes-saved">Saved</span>}
        {error && <span className="settings-save-error" style={{ margin: 0 }}>{error}</span>}
        <button type="button" className="admin-household-notes-edit" onClick={startEditing}>
          {notes ? 'Edit' : '+'}
        </button>
      </div>
    );
  }

  return (
    <div className="admin-household-notes-editor">
      <textarea
        value={draftNotes}
        onChange={e => setDraftNotes(e.target.value)}
        rows={2}
        placeholder="Internal notes visible only to operators…"
        className="admin-household-notes-textarea"
        autoFocus
      />
      {error && <p className="settings-save-error" style={{ margin: 0 }}>{error}</p>}
      <div className="admin-household-notes-actions">
        <button type="button" onClick={save} disabled={saving} className="btn-primary btn-sm">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={cancelEditing} className="btn-ghost btn-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

function HouseholdsSection() {
  const [households, setHouseholds] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [openNonce, setOpenNonce] = useState(0);
  const debounceRef = useRef(null);

  // Create Household form
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createRole, setCreateRole] = useState('admin');
  const [createEmail, setCreateEmail] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createdResult, setCreatedResult] = useState(null);
  const [createQrSheet, setCreateQrSheet] = useState(null);

  const load = useCallback(async (query = '', off = 0, append = false) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '50', offset: String(off) });
      if (query) params.set('q', query);
      const d = await apiJson(`/operator/households?${params}`);
      setHouseholds(prev => append ? [...prev, ...(d.households || [])] : (d.households || []));
      setTotal(d.total ?? null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const newQ = e.detail?.q || '';
      setQ(newQ);
      load(newQ, 0);
      setOpenNonce(n => n + 1);
    };
    document.addEventListener('admin:filter-households', handler);
    return () => document.removeEventListener('admin:filter-households', handler);
  }, [load]);

  const handleSearch = (val) => {
    setQ(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(val, 0), 350);
  };

  const loadMore = () => {
    load(q, households.length, true);
  };

  function openCreate() {
    setShowCreate(true);
    setCreatedResult(null);
    setCreateError(null);
    setCreateName('');
    setCreateRole('admin');
    setCreateEmail('');
  }

  function closeCreate() {
    setShowCreate(false);
    setCreatedResult(null);
    setCreateError(null);
    setCreateQrSheet(null);
  }

  async function handleCreateHousehold(e) {
    e.preventDefault();
    setCreateBusy(true);
    setCreateError(null);
    try {
      const res = await apiFetch('/operator/households', {
        method: 'POST',
        body: JSON.stringify({
          name: createName.trim(),
          initial_invite_role: createRole,
          invited_email: createEmail.trim() || null,
        }),
      });
      if (!res.ok) {
        if (res.status === 429) throw new Error("Daily email invite limit reached. Use 'Share a code' instead.");
        throw new Error(await readApiError(res));
      }
      const data = await res.json();
      setCreatedResult({ ...data, invited_email: createEmail.trim() || null });
      setCreateName('');
      setCreateRole('admin');
      setCreateEmail('');
      load(q, 0);
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreateBusy(false);
    }
  }

  return (
    <Accordion
      id="admin-households"
      title="Households"
      defaultOpen={false}
      badge={total ?? null}
      onFirstOpen={() => load(q, 0)}
      forceOpen={openNonce}
    >
      {/* Create Household */}
      <div style={{ padding: '0.5rem 0', borderBottom: '1px solid var(--border)' }}>
        {!showCreate && (
          <button type="button" className="settings-lookup-action" onClick={openCreate}>
            Create Household
          </button>
        )}
        {showCreate && !createdResult && (
          <form onSubmit={handleCreateHousehold}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <label className="form-field" style={{ margin: 0 }}>
                <span className="form-label">Household name</span>
                <input
                  type="text"
                  className="form-input"
                  value={createName}
                  onChange={e => setCreateName(e.target.value)}
                  autoComplete="off"
                  required
                />
              </label>
              <div>
                <div className="settings-field-label">Initial invite role</div>
                <div className="settings-segment" style={{ maxWidth: 200 }}>
                  <button
                    type="button"
                    className={`settings-segment-btn ${createRole === 'admin' ? 'settings-segment-btn--active' : ''}`}
                    onClick={() => setCreateRole('admin')}
                  >
                    Admin
                  </button>
                  <button
                    type="button"
                    className={`settings-segment-btn ${createRole === 'member' ? 'settings-segment-btn--active' : ''}`}
                    onClick={() => setCreateRole('member')}
                  >
                    Member
                  </button>
                </div>
              </div>
              <label className="form-field" style={{ margin: 0 }}>
                <span className="form-label">Send invite email to (optional)</span>
                <input
                  type="email"
                  className="form-input"
                  value={createEmail}
                  onChange={e => setCreateEmail(e.target.value)}
                  autoComplete="email"
                />
              </label>
              <p className="settings-empty-note">You will not be added as a member of this household</p>
              {createError && <p className="settings-save-error" style={{ margin: 0 }}>{createError}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button type="submit" className="settings-lookup-action" disabled={createBusy || !createName.trim()}>
                  {createBusy ? 'Creating...' : 'Create Household'}
                </button>
                <button type="button" className="settings-lookup-action settings-lookup-action--ghost" onClick={closeCreate} disabled={createBusy}>
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}
        {showCreate && createdResult && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <p style={{ margin: 0, fontSize: '0.85rem' }}>
              Household created: <strong>{createdResult.household.name}</strong>
            </p>
            {createdResult.invited_email && (
              <p className="settings-save-status" style={{ margin: 0 }}>
                Invite created for {createdResult.invited_email}
              </p>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <code style={{ fontSize: '0.9rem', fontWeight: 700, letterSpacing: '0.06em', background: 'var(--surface-recessed)', padding: '0.2rem 0.5rem', borderRadius: 6 }}>
                {createdResult.invite_code || createdResult.join_code}
              </code>
              <CopyButton text={createdResult.invite_code || createdResult.join_code} label="Copy code" />
              <CopyButton text={createdResult.invite_url || inviteUrlForCode(createdResult.invite_code || createdResult.join_code)} label="Copy link" />
              <button
                type="button"
                className="settings-lookup-action"
                onClick={() => setCreateQrSheet({
                  code: createdResult.invite_code || createdResult.join_code,
                  inviteUrl: createdResult.invite_url || inviteUrlForCode(createdResult.invite_code || createdResult.join_code),
                  householdName: createdResult.household.name,
                })}
              >
                QR code
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="settings-lookup-action" onClick={openCreate}>
                Create another
              </button>
              <button type="button" className="settings-lookup-action settings-lookup-action--ghost" onClick={closeCreate}>
                Done
              </button>
            </div>
          </div>
        )}
        {createQrSheet && (
          <QRCodeSheet
            code={createQrSheet.code}
            inviteUrl={createQrSheet.inviteUrl}
            householdName={createQrSheet.householdName}
            onClose={() => setCreateQrSheet(null)}
          />
        )}
      </div>

      {/* Household list */}
      <input
        className="form-input"
        placeholder="Search households…"
        value={q}
        onChange={e => handleSearch(e.target.value)}
        style={{ marginBottom: '0.5rem', width: '100%' }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.25rem' }}>
        <button type="button" className="settings-lookup-action" onClick={() => load(q, 0)} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {error && <p className="settings-save-error">{error}</p>}
      {loading && households.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</p>}
      {households.length > 0 && (
        <>
          <p style={{ fontSize: '0.77rem', color: 'var(--text-muted)', margin: '0 0 0.4rem' }}>
            Showing {households.length} of {total ?? '…'} household{total !== 1 ? 's' : ''}
            {loading ? ' · refreshing…' : ''}
          </p>
          {households.map(h => {
            const daysSince = (iso) => iso
              ? Math.floor((Date.now() - new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z').getTime()) / 86400000)
              : Infinity;
            const isDormant = daysSince(h.last_member_seen_at) >= 30;

            return (
              <div key={h.id} style={{ padding: '0.75rem 0', borderBottom: '1px solid var(--border)' }}>

                {/* Name + dormancy */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{h.name}</span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>#{h.id}</span>
                  {isDormant && <span className="admin-chip admin-chip--warn">Dormant</span>}
                </div>

                {/* Owner / members / created */}
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <span>{h.member_count} member{h.member_count !== 1 ? 's' : ''}</span>
                  {h.owner_email && <span>Owner: {h.owner_email}</span>}
                  <span>Created {formatDate(h.created_at)}</span>
                </div>

                {/* Usage stats */}
                <div className="admin-household-stats">
                  <span>
                    {h.total_plant_count} plant{h.total_plant_count !== 1 ? 's' : ''}
                    {h.total_plant_count !== h.plant_count ? ` (${h.plant_count} active)` : ''}
                  </span>
                  <span>{h.photo_count} photo{h.photo_count !== 1 ? 's' : ''}</span>
                  <span>{h.obs_30d} care log{h.obs_30d !== 1 ? 's' : ''} (30d)</span>
                  <span>Last care: {h.last_observation_at ? formatRelativeTime(h.last_observation_at) : 'never'}</span>
                  <span>Last seen: {h.last_member_seen_at ? formatRelativeTime(h.last_member_seen_at) : 'never'}</span>
                </div>

                {/* Feature adoption milestones */}
                <div className="admin-household-milestones">
                  <MilestoneChip done={h.total_plant_count > 0} label="Plant" />
                  <MilestoneChip done={h.photo_count > 0} label="Photo" />
                  <MilestoneChip done={!!h.last_observation_at} label="Care log" />
                  <MilestoneChip done={!!h.has_rounds} label="Rounds" />
                  <MilestoneChip done={!!h.has_propagation} label="Propagation" />
                </div>

                {/* Operator notes */}
                <HouseholdNotes householdId={h.id} initialNotes={h.operator_notes} />

              </div>
            );
          })}
          {total !== null && households.length < total && (
            <button type="button" className="settings-lookup-action" onClick={loadMore} disabled={loading} style={{ marginTop: '0.5rem' }}>
              {loading ? '…' : 'Load more'}
            </button>
          )}
        </>
      )}
      {!loading && households.length === 0 && <p className="settings-empty-note">No households.</p>}
    </Accordion>
  );
}

// ─── Sessions Section ──────────────────────────────────────────────────────

function parseUA(ua) {
  if (!ua) return null;
  if (/iPhone|iPad/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'macOS';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown';
}

function parseBrowser(ua) {
  if (!ua) return null;
  if (/Chrome/.test(ua) && !/Edg|OPR/.test(ua)) return 'Chrome';
  if (/Safari/.test(ua) && !/Chrome/.test(ua)) return 'Safari';
  if (/Firefox/.test(ua)) return 'Firefox';
  if (/Edg/.test(ua)) return 'Edge';
  return 'Browser';
}

function SessionsSection() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState({});
  const [cleanupResult, setCleanupResult] = useState(null);
  const [openNonce, setOpenNonce] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiJson('/operator/sessions');
      setRows(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = () => setOpenNonce(n => n + 1);
    document.addEventListener('admin:open-sessions', handler);
    return () => document.removeEventListener('admin:open-sessions', handler);
  }, []);

  const revokeSession = async (sessionId) => {
    setBusy(b => ({ ...b, [sessionId]: true }));
    try {
      const res = await apiFetch(`/operator/sessions/${sessionId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await readApiError(res));
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(b => ({ ...b, [sessionId]: false }));
    }
  };

  const cleanupExpired = async () => {
    setBusy(b => ({ ...b, cleanup: true }));
    setCleanupResult(null);
    try {
      const data = await apiJson('/operator/sessions', { method: 'DELETE' });
      setError(null);
      await load();
      if (data.deleted_count > 0) {
        setCleanupResult(`Deleted ${data.deleted_count} expired session${data.deleted_count !== 1 ? 's' : ''}.`);
        setTimeout(() => setCleanupResult(null), 4000);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(b => ({ ...b, cleanup: false }));
    }
  };

  return (
    <Accordion id="admin-sessions" title="Active Sessions" badge={rows ? rows.length : null} forceOpen={openNonce}>
      <div style={{ marginBottom: '0.5rem' }}>
        <button type="button" className="settings-lookup-action" onClick={cleanupExpired} disabled={busy.cleanup}>
          {busy.cleanup ? '…' : 'Clean Up Expired'}
        </button>
      </div>
      {cleanupResult && <p className="settings-save-status">{cleanupResult}</p>}
      {error && <p className="settings-save-error">{error}</p>}
      {loading && !rows && <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</p>}
      {rows && rows.length === 0 && <p className="settings-empty-note">No active sessions.</p>}
      {rows && rows.map(s => {
        const os = parseUA(s.user_agent);
        const browser = parseBrowser(s.user_agent);
        const deviceStr = browser && os ? `${browser} · ${os}` : (browser || os || null);
        return (
          <div key={s.session_id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border)', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{s.display_name || s.email}</div>
              {s.display_name && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{s.email}</div>}
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                {s.household_name && <span>{s.household_name} &bull; </span>}
                {s.ip_address && <span>{s.ip_address} &bull; </span>}
                {deviceStr && <span>{deviceStr} &bull; </span>}
                Last seen {formatRelativeTime(s.last_seen_at || s.created_at)} &bull; Expires {formatFutureTime(s.expires_at)}
              </div>
            </div>
            <ConfirmButton
              label="Revoke"
              confirmLabel="Revoke this session"
              busy={busy[s.session_id]}
              onConfirm={() => revokeSession(s.session_id)}
            />
          </div>
        );
      })}
    </Accordion>
  );
}

// ─── Audit Log Section ─────────────────────────────────────────────────────

function SearchCombobox({ placeholder, fetchSuggestions, renderLabel, renderSublabel, value, onChange }) {
  const [inputVal, setInputVal] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = (q) => {
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await fetchSuggestions(q);
        setSuggestions(results);
        setOpen(true);
      } catch { setSuggestions([]); }
    }, 250);
  };

  const select = (item) => {
    setInputVal(renderLabel(item));
    setSuggestions([]);
    setOpen(false);
    onChange(String(item.id));
  };

  const clear = () => {
    setInputVal('');
    setSuggestions([]);
    setOpen(false);
    onChange('');
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', minWidth: 180 }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          className="form-input"
          value={inputVal}
          placeholder={placeholder}
          onChange={e => { setInputVal(e.target.value); onChange(''); search(e.target.value); }}
          onFocus={() => { if (suggestions.length) setOpen(true); }}
          style={{ fontSize: '0.82rem', padding: '0.3rem 0.5rem', paddingRight: value ? '1.6rem' : '0.5rem', width: '100%' }}
        />
        {value && (
          <button
            type="button"
            onClick={clear}
            style={{ position: 'absolute', right: '0.35rem', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1, padding: 0 }}
            aria-label="Clear"
            title="Clear"
          >×</button>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 100, maxHeight: 200, overflowY: 'auto' }}>
          {suggestions.map(item => (
            <div
              key={item.id}
              onMouseDown={() => select(item)}
              style={{ padding: '0.45rem 0.65rem', cursor: 'pointer', fontSize: '0.82rem', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2, var(--surface-recessed))'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ fontWeight: 500, color: 'var(--text)' }}>{renderLabel(item)}</div>
              {renderSublabel && <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{renderSublabel(item)}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const AUDIT_CATEGORY_CHIPS = [
  { key: '', label: 'All' },
  { key: 'plant', label: 'Plant' },
  { key: 'observation', label: 'Observation' },
  { key: 'measurement', label: 'Measurement' },
  { key: 'photo', label: 'Photo' },
  { key: 'household', label: 'Household' },
  { key: 'lookup', label: 'Lookup Data' },
  { key: 'finance', label: 'Finance' },
  { key: 'system', label: 'System' },
];

function AuditSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [householdFilter, setHouseholdFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [limit, setLimit] = useState(100);

  const load = useCallback(async (category = '', action = '', userId = '', householdId = '', from = '', to = '', lim = 100) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(lim) });
      if (category) params.set('category', category);
      else if (action) params.set('action', action);
      if (userId) params.set('user_id', userId);
      if (householdId) params.set('household_id', householdId);
      if (from) params.set('from_date', from);
      if (to) params.set('to_date', to);
      const d = await apiJson(`/operator/audit?${params}`);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ACTION_GROUPS = [
    { label: null, options: [{ value: '', label: 'Specific action…' }] },
    { label: 'Plant', options: [
      { value: 'plant_created', label: 'created' },
      { value: 'plant_updated', label: 'updated' },
      { value: 'plant_deleted', label: 'deleted' },
    ]},
    { label: 'Observation', options: [
      { value: 'observation_created', label: 'created' },
      { value: 'observation_updated', label: 'updated' },
      { value: 'observation_deleted', label: 'deleted' },
    ]},
    { label: 'Measurement', options: [
      { value: 'measurement_created', label: 'created' },
      { value: 'measurement_updated', label: 'updated' },
      { value: 'measurement_deleted', label: 'deleted' },
    ]},
    { label: 'Photo', options: [
      { value: 'photo_uploaded', label: 'uploaded' },
      { value: 'photo_deleted', label: 'deleted' },
    ]},
    { label: 'Household', options: [
      { value: 'household_renamed', label: 'renamed' },
      { value: 'member_removed', label: 'member removed' },
      { value: 'member_role_changed', label: 'role changed' },
      { value: 'join_code_created', label: 'invite code created' },
      { value: 'join_code_revoked', label: 'invite code revoked' },
    ]},
    { label: 'Lookup Data', options: [
      { value: 'location_created', label: 'location created' },
      { value: 'location_renamed', label: 'location renamed' },
      { value: 'location_deleted', label: 'location deleted' },
      { value: 'tag_created', label: 'tag created' },
      { value: 'tag_renamed', label: 'tag renamed' },
      { value: 'tag_deleted', label: 'tag deleted' },
      { value: 'light_source_created', label: 'light source created' },
      { value: 'light_source_renamed', label: 'light source renamed' },
      { value: 'light_source_deleted', label: 'light source deleted' },
      { value: 'field_option_created', label: 'field option created' },
      { value: 'field_option_renamed', label: 'field option renamed' },
      { value: 'field_option_deleted', label: 'field option deleted' },
      { value: 'growing_method_deleted', label: 'growing method deleted' },
      { value: 'substrate_deleted', label: 'substrate deleted' },
    ]},
    { label: 'Finance', options: [
      { value: 'price_logged', label: 'price logged' },
      { value: 'price_deleted', label: 'price deleted' },
      { value: 'sale_logged', label: 'sale logged' },
      { value: 'sale_deleted', label: 'sale deleted' },
    ]},
    { label: 'System', options: [
      { value: 'setting_updated', label: 'setting updated' },
      { value: 'session_revoked', label: 'session revoked' },
    ]},
  ];

  const chipStyle = (active) => ({
    padding: '0.25rem 0.6rem',
    borderRadius: 20,
    border: '1px solid',
    borderColor: active ? 'var(--green)' : 'var(--border)',
    background: active ? 'var(--green)' : 'transparent',
    color: active ? '#fff' : 'var(--text-muted)',
    fontSize: '0.78rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  });

  const applyFilters = (overrides = {}) => {
    const cat = overrides.category !== undefined ? overrides.category : categoryFilter;
    const act = overrides.action !== undefined ? overrides.action : actionFilter;
    const uid = overrides.userId !== undefined ? overrides.userId : userFilter;
    const hid = overrides.householdId !== undefined ? overrides.householdId : householdFilter;
    const from = overrides.fromDate !== undefined ? overrides.fromDate : fromDate;
    const to = overrides.toDate !== undefined ? overrides.toDate : toDate;
    load(cat, act, uid, hid, from, to, limit);
  };

  const loadMore = () => {
    const newLimit = limit + 100;
    setLimit(newLimit);
    load(categoryFilter, actionFilter, userFilter, householdFilter, fromDate, toDate, newLimit);
  };

  return (
    <Accordion title="Audit Log" badge={data ? data.total : null}>
      {/* Category chips */}
      <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
        {AUDIT_CATEGORY_CHIPS.map(c => (
          <button
            key={c.key}
            type="button"
            style={chipStyle(categoryFilter === c.key && !actionFilter)}
            onClick={() => {
              setCategoryFilter(c.key);
              setActionFilter('');
              applyFilters({ category: c.key, action: '' });
            }}
          >
            {c.label}
          </button>
        ))}
      </div>
      {/* Row 2: action + user + household + refresh */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          className="form-input"
          value={actionFilter}
          onChange={e => {
            setActionFilter(e.target.value);
            setCategoryFilter('');
            applyFilters({ action: e.target.value, category: '' });
          }}
          style={{ fontSize: '0.82rem', padding: '0.3rem 0.5rem' }}
        >
          {ACTION_GROUPS.map(g => g.label
            ? <optgroup key={g.label} label={g.label}>
                {g.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </optgroup>
            : g.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)
          )}
        </select>
        <SearchCombobox
          placeholder="Filter by user…"
          value={userFilter}
          fetchSuggestions={q => apiJson(`/operator/users?q=${encodeURIComponent(q)}&limit=10`).then(d => d.users || [])}
          renderLabel={u => u.display_name ? `${u.display_name} (${u.email})` : u.email}
          renderSublabel={u => u.display_name ? u.email : null}
          onChange={uid => { setUserFilter(uid); applyFilters({ userId: uid }); }}
        />
        <SearchCombobox
          placeholder="Filter by household…"
          value={householdFilter}
          fetchSuggestions={q => apiJson(`/operator/households?q=${encodeURIComponent(q)}&limit=10`).then(d => d.households || [])}
          renderLabel={h => `${h.name} #${h.id}`}
          renderSublabel={h => h.owner_email ? `Owner: ${h.owner_email}` : null}
          onChange={hid => { setHouseholdFilter(hid); applyFilters({ householdId: hid }); }}
        />
        <button type="button" className="settings-lookup-action" onClick={() => applyFilters()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {/* Row 3: date range */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            type="date"
            className="form-input"
            value={fromDate}
            onChange={e => { setFromDate(e.target.value); applyFilters({ fromDate: e.target.value }); }}
            placeholder="From"
            style={{ fontSize: '0.82rem', padding: '0.3rem 0.5rem', paddingRight: fromDate ? '1.6rem' : '0.5rem' }}
          />
          {fromDate && (
            <button type="button" onClick={() => { setFromDate(''); applyFilters({ fromDate: '' }); }} style={{ position: 'absolute', right: '0.35rem', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1, padding: 0 }} aria-label="Clear from date">×</button>
          )}
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            type="date"
            className="form-input"
            value={toDate}
            onChange={e => { setToDate(e.target.value); applyFilters({ toDate: e.target.value }); }}
            placeholder="To"
            style={{ fontSize: '0.82rem', padding: '0.3rem 0.5rem', paddingRight: toDate ? '1.6rem' : '0.5rem' }}
          />
          {toDate && (
            <button type="button" onClick={() => { setToDate(''); applyFilters({ toDate: '' }); }} style={{ position: 'absolute', right: '0.35rem', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1, padding: 0 }} aria-label="Clear to date">×</button>
          )}
        </div>
      </div>
      {error && <p className="settings-save-error">{error}</p>}
      {loading && !data && <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</p>}
      {data && (
        <>
          <p style={{ fontSize: '0.77rem', color: 'var(--text-muted)', margin: '0 0 0.4rem' }}>
            Showing {data.events.length} event{data.events.length !== 1 ? 's' : ''}
            {loading ? ' · refreshing…' : ''}
          </p>
          {data.events.length === 0 && <p className="settings-empty-note">No audit events found.</p>}
          {data.events.map(e => (
            <div key={e.id} style={{ padding: '0.45rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.82rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: 'var(--text)', fontFamily: 'monospace', fontSize: '0.78rem', background: 'var(--surface-recessed)', borderRadius: '3px', padding: '1px 5px' }}>{e.action}</span>
                {e.household_name && <span style={{ color: 'var(--text-muted)' }}>{e.household_name} <span style={{ opacity: 0.6 }}>#{e.household_id}</span></span>}
                <span style={{ color: 'var(--text-muted)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>{formatRelativeTime(e.created_at)}</span>
              </div>
              {e.summary && <div style={{ color: 'var(--text-muted)', marginTop: '0.15rem' }}>{e.summary}</div>}
              {e.actor && <div style={{ color: 'var(--text-light, var(--text-muted))', marginTop: '0.1rem' }}>by {e.actor.label}</div>}
            </div>
          ))}
          {data.events.length === limit && (
            <button type="button" className="settings-lookup-action" onClick={loadMore} disabled={loading} style={{ marginTop: '0.5rem' }}>
              {loading ? '…' : 'Load more'}
            </button>
          )}
        </>
      )}
    </Accordion>
  );
}

// ─── Migrations Section ────────────────────────────────────────────────────

function MigrationsSection() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiJson('/operator/migrations')
      .then(data => { setRows(data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  return (
    <Accordion title="Migrations" badge={rows ? rows.length : null}>
      {error && <p className="settings-save-error">{error}</p>}
      {loading && <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</p>}
      {rows && rows.map(m => (
        <div key={m.filename} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.8rem', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{m.filename}</span>
          <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatDate(m.applied_at)}</span>
        </div>
      ))}
    </Accordion>
  );
}

// ─── Infrastructure Section ────────────────────────────────────────────────

function PctBar({ pct, warn = 75, danger = 90 }) {
  const color = pct >= danger ? 'var(--danger)' : pct >= warn ? 'var(--warning, #e09c3c)' : 'var(--green)';
  return (
    <div style={{ background: 'var(--border)', borderRadius: 4, height: 6, overflow: 'hidden', marginTop: 4 }}>
      <div style={{ width: `${Math.min(pct, 100)}%`, background: color, height: '100%', transition: 'width 0.4s' }} />
    </div>
  );
}

function InfrastructureSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vacuuming, setVacuuming] = useState(false);
  const [vacuumResult, setVacuumResult] = useState(null);
  const [vacuumError, setVacuumError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiJson('/operator/infrastructure')
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const runVacuum = async () => {
    setVacuuming(true);
    setVacuumResult(null);
    setVacuumError(null);
    try {
      const res = await apiJson('/operator/vacuum', { method: 'POST' });
      setVacuumResult(res);
      load();
    } catch (e) {
      setVacuumError(e.message);
    } finally {
      setVacuuming(false);
    }
  };

  if (loading) return <SectionCard title="Infrastructure"><p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</p></SectionCard>;
  if (error) return <SectionCard title="Infrastructure"><p className="settings-save-error">{error}</p></SectionCard>;

  const { system, storage, database, engagement, health } = data;
  const statusColor = health.status === 'red' ? 'var(--danger)' : health.status === 'yellow' ? 'var(--warning, #e09c3c)' : 'var(--green)';
  const memUsedPct = system.mem_total_bytes && system.mem_available_bytes
    ? Math.round((1 - system.mem_available_bytes / system.mem_total_bytes) * 100)
    : null;
  const uptimeH = system.uptime_seconds ? Math.floor(system.uptime_seconds / 3600) : null;
  const uptimeStr = uptimeH !== null ? (uptimeH >= 48 ? `${Math.floor(uptimeH / 24)}d` : `${uptimeH}h`) : null;

  return (
    <SectionCard title="Infrastructure">
      {/* Health banner */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.6rem 0.75rem', background: 'var(--surface-2, var(--bg))', border: `1px solid ${statusColor}`, borderRadius: 8, marginBottom: '1rem' }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: statusColor, marginTop: 3, flexShrink: 0 }} />
        <div>
          {health.signals.map((s, i) => (
            <div key={i} style={{ fontSize: '0.82rem', color: i === 0 && health.status !== 'green' ? statusColor : 'var(--text-muted)' }}>{s}</div>
          ))}
        </div>
      </div>

      {/* Pi System Health */}
      <Accordion title="Pi System Health">
        {system.cpu_pct !== null ? (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>CPU</span>
              <span style={{ color: 'var(--text)' }}>{system.cpu_pct}%</span>
            </div>
            <PctBar pct={system.cpu_pct} warn={70} danger={90} />
          </div>
        ) : (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>CPU — N/A (not Linux)</p>
        )}
        {memUsedPct !== null ? (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>Memory</span>
              <span style={{ color: 'var(--text)' }}>{memUsedPct}% of {formatBytes(system.mem_total_bytes)}</span>
            </div>
            <PctBar pct={memUsedPct} warn={75} danger={90} />
          </div>
        ) : (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Memory — N/A (not Linux)</p>
        )}
        <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.82rem', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
          {uptimeStr && <span>Uptime: <strong style={{ color: 'var(--text)' }}>{uptimeStr}</strong></span>}
          {system.load_1m !== null && <span>Load 1m: <strong style={{ color: 'var(--text)' }}>{system.load_1m}</strong></span>}
        </div>
      </Accordion>

      {/* Storage */}
      <Accordion title="Storage">
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
            <span style={{ color: 'var(--text-muted)' }}>Disk used</span>
            <span style={{ color: 'var(--text)' }}>{formatBytes(storage.disk_used_bytes)} / {formatBytes(storage.disk_total_bytes)} ({storage.disk_used_pct}%)</span>
          </div>
          <PctBar pct={storage.disk_used_pct} warn={75} danger={90} />
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
          <span>Photos: <strong style={{ color: 'var(--text)' }}>{formatBytes(storage.photo_dir_bytes)}</strong> ({storage.photo_count_fs} files)</span>
          <span>DB: <strong style={{ color: 'var(--text)' }}>{formatBytes(storage.db_size_bytes)}</strong></span>
          {storage.wal_size_bytes > 0 && <span>WAL: <strong style={{ color: 'var(--text)' }}>{formatBytes(storage.wal_size_bytes)}</strong></span>}
        </div>
        {storage.growth_bytes_per_day != null && (
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            Growth: ~<strong style={{ color: 'var(--text)' }}>{formatBytes(storage.growth_bytes_per_day)}/day</strong>
            {storage.days_to_80pct != null && (
              <span> · 80% disk in <strong style={{ color: storage.days_to_80pct < 30 ? 'var(--danger)' : storage.days_to_80pct < 90 ? 'var(--warning, #e09c3c)' : 'var(--green)' }}>~{storage.days_to_80pct}d</strong></span>
            )}
          </div>
        )}
      </Accordion>

      {/* Database Health */}
      <Accordion title="Database Health">
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
          <span>Pages: <strong style={{ color: 'var(--text)' }}>{database.page_count.toLocaleString()}</strong></span>
          <span>Free pages: <strong style={{ color: 'var(--text)' }}>{database.freelist_count.toLocaleString()}</strong></span>
          <span>Fragmentation: <strong style={{ color: database.frag_pct >= 20 ? 'var(--warning, #e09c3c)' : 'var(--green)' }}>{database.frag_pct}%</strong></span>
        </div>
        <ConfirmButton
          label="Run VACUUM"
          confirmLabel="Confirm VACUUM"
          onConfirm={runVacuum}
          disabled={vacuuming}
          style={{ fontSize: '0.82rem', padding: '0.35rem 0.75rem' }}
        />
        {vacuuming && <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>Running VACUUM…</p>}
        {vacuumResult && (
          <p style={{ fontSize: '0.82rem', color: 'var(--green)', marginTop: '0.4rem' }}>
            Done in {vacuumResult.duration_ms}ms · saved {formatBytes(vacuumResult.saved_bytes)}
          </p>
        )}
        {vacuumError && <p style={{ fontSize: '0.82rem', color: 'var(--danger)', marginTop: '0.4rem' }}>{vacuumError}</p>}
      </Accordion>

      {/* Engagement */}
      <Accordion title="Engagement">
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
          <StatCard label="DAU" value={engagement.dau} />
          <StatCard label="WAU" value={engagement.wau} />
          <StatCard label="MAU" value={engagement.mau} />
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
          <span>Observations (7d): <strong style={{ color: 'var(--text)' }}>{engagement.observations_7d}</strong></span>
          <span>Photos (7d): <strong style={{ color: 'var(--text)' }}>{engagement.photos_7d}</strong></span>
        </div>
      </Accordion>
    </SectionCard>
  );
}

// ─── Care Profiles Section ─────────────────────────────────────────────────

const CARE_PROFILE_STATUS_TABS = ['draft', 'approved', 'stale', 'rejected'];
const CARE_PROFILE_PRESETS = [
  { key: 'all', label: 'All' },
  { key: 'minor_edits', label: 'Minor edits' },
  { key: 'major_review', label: 'Major review' },
  { key: 'low_confidence', label: 'Low confidence' },
  { key: 'recent', label: 'Recent' },
];
const CARE_PROFILE_SORTS = [
  { value: 'created_desc', label: 'Newest created' },
  { value: 'created_asc', label: 'Oldest created' },
  { value: 'generated_desc', label: 'Newest generated' },
  { value: 'priority', label: 'Review priority' },
  { value: 'confidence', label: 'Confidence low to high' },
  { value: 'name', label: 'Name A-Z' },
];
const SORT_PARAM_MAP = {
  created_desc:   { sort_by: 'created_at',   sort_dir: 'desc' },
  created_asc:    { sort_by: 'created_at',   sort_dir: 'asc'  },
  generated_desc: { sort_by: 'generated_at', sort_dir: 'desc' },
  name:           { sort_by: 'name',         sort_dir: 'asc'  },
  priority:       { sort_by: 'priority',     sort_dir: 'asc'  },
  confidence:     { sort_by: 'confidence',   sort_dir: 'asc'  },
};

function profileNeedsTraitReview(profile) {
  if (!profile.identification_traits) return false;
  try {
    const traits = typeof profile.identification_traits === 'string'
      ? JSON.parse(profile.identification_traits)
      : profile.identification_traits;
    const flags = traits._review_flags || {};
    return Object.keys(flags).some(k => flags[k]);
  } catch {
    return false;
  }
}

const VERDICT_STYLE = {
  approved:            { color: 'var(--green, #3a7d44)' },
  needs_minor_edits:   { color: '#b87400' },
  needs_major_review:  { color: 'var(--danger)' },
  reject:              { color: 'var(--danger)' },
};

function ReviewSection({ title, section, flagged = false }) {
  const concerns = section?.concerns || [];
  const suggested = section?.suggested_changes || [];
  if (concerns.length === 0 && suggested.length === 0) return null;
  return (
    <div style={{ marginBottom: '0.6rem' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: flagged ? 'var(--danger)' : 'var(--text-muted)', marginBottom: '0.25rem' }}>
        {title}
      </div>
      {concerns.map((c, i) => (
        <div key={i} style={{ fontSize: '0.81rem', color: 'var(--text)', marginBottom: '0.2rem', paddingLeft: '0.75rem' }}>• {c}</div>
      ))}
      {suggested.map((c, i) => (
        <div key={i} style={{ fontSize: '0.81rem', color: '#b87400', marginBottom: '0.2rem', paddingLeft: '0.75rem' }}>→ {c}</div>
      ))}
    </div>
  );
}

// ── Form helpers ─────────────────────────────────────────────────────────────

function profileToForm(p) {
  const s = v => v ?? '';
  const guidedKeys = new Set([
    'common_names','distinguishing_notes','care_difficulty','growth_rate','light','water','humidity',
    'temperature','soil','fertilization','growth_habit','propagation_methods','dormancy',
    'common_pests','common_problems','toxicity','outdoor_care',
    'cultivar_notes','grower_notes','profile_notes',
  ]);
  const rawOnlyFields = Object.fromEntries(
    Object.entries(p || {}).filter(([key]) => !guidedKeys.has(key))
  );
  return {
    _rawOnlyFields: rawOnlyFields,
    common_names: p.common_names ?? [],
    distinguishing_notes: s(p.distinguishing_notes),
    care_difficulty: p.care_difficulty ?? 'intermediate',
    growth_rate: p.growth_rate ?? 'moderate',
    light: { level: p.light?.level ?? 'medium', lux: s(p.light?.lux), description: s(p.light?.description) },
    water: { frequency: s(p.water?.frequency), dry_preference: p.water?.dry_preference ?? 'slightly_moist', description: s(p.water?.description) },
    humidity: { min_percent: p.humidity?.min_percent ?? '', ideal_range: s(p.humidity?.ideal_range), description: s(p.humidity?.description) },
    temperature: {
      min_f: p.temperature?.min_f ?? '', max_f: p.temperature?.max_f ?? '',
      min_c: p.temperature?.min_c ?? '', max_c: p.temperature?.max_c ?? '',
      description: s(p.temperature?.description),
    },
    soil: { drainage_preference: p.soil?.drainage_preference ?? 'well_draining', epiphytic: p.soil?.epiphytic ?? false, description: s(p.soil?.description) },
    fertilization: { frequency: s(p.fertilization?.frequency), sensitivity: p.fertilization?.sensitivity ?? 'moderate', notes: s(p.fertilization?.notes) },
    growth_habit: {
      types: Array.isArray(p.growth_habit?.type) ? p.growth_habit.type : (p.growth_habit?.type ? [p.growth_habit.type] : []),
      mature_size_indoor: s(p.growth_habit?.mature_size_indoor),
      support_type: s(p.growth_habit?.support?.benefits_from ?? p.growth_habit?.support?.type),
      support_notes: s(p.growth_habit?.support?.notes),
      leaf_shape: s(p.growth_habit?.leaf_shape),
      leaf_texture: s(p.growth_habit?.leaf_texture),
      venation: s(p.growth_habit?.venation),
      petiole: s(p.growth_habit?.petiole),
      stem_growth_form: s(p.growth_habit?.stem_growth_form),
      diagnostic_features: Array.isArray(p.growth_habit?.diagnostic_features) ? p.growth_habit.diagnostic_features : [],
    },
    dormancy: s(p.dormancy),
    propagation_methods: (p.propagation_methods ?? []).map(m => ({ method: m.method ?? 'stem_cutting', success_rate: m.success_rate ?? 'moderate', notes: s(m.notes) })),
    common_pests: p.common_pests ?? [],
    common_problems: (p.common_problems ?? []).map(pr => ({ issue: s(pr.issue), cause: s(pr.cause), solution: s(pr.solution) })),
    toxicity: { toxic: p.toxicity?.toxic ?? false, severity: s(p.toxicity?.severity), affected: s(p.toxicity?.affected), description: s(p.toxicity?.description) },
    outdoor_suitable: p.outdoor_care?.outdoor_suitable ?? false,
    outdoor_sun_tolerance: p.outdoor_care?.sun_tolerance ?? 'none',
    outdoor_min_temp_f: p.outdoor_care?.min_outdoor_temp_f ?? '',
    outdoor_min_temp_c: p.outdoor_care?.min_outdoor_temp_c ?? '',
    outdoor_acclimation_notes: s(p.outdoor_care?.acclimation_notes),
    outdoor_risks: s(p.outdoor_care?.outdoor_risks),
    cultivar_notes: s(p.cultivar_notes),
    grower_notes: s(p.grower_notes),
    profile_notes: s(p.profile_notes),
  };
}

function formToProfile(f) {
  const ne = v => v === '' ? null : v;
  const ni = v => (v === '' || v == null) ? null : parseInt(v);
  return {
    ...(f._rawOnlyFields || {}),
    ...(f.common_names?.length > 0 ? { common_names: f.common_names } : {}),
    distinguishing_notes: ne(f.distinguishing_notes),
    care_difficulty: f.care_difficulty,
    growth_rate: f.growth_rate,
    light: f.light,
    water: f.water,
    humidity: { min_percent: ni(f.humidity.min_percent), ideal_range: ne(f.humidity.ideal_range), description: ne(f.humidity.description) },
    temperature: { min_f: ni(f.temperature.min_f), max_f: ni(f.temperature.max_f), min_c: ni(f.temperature.min_c), max_c: ni(f.temperature.max_c), description: ne(f.temperature.description) },
    soil: f.soil,
    fertilization: f.fertilization,
    growth_habit: {
      type: f.growth_habit.types,
      mature_size_indoor: ne(f.growth_habit.mature_size_indoor),
      support: (f.growth_habit.support_type || f.growth_habit.support_notes)
        ? { benefits_from: ne(f.growth_habit.support_type), notes: ne(f.growth_habit.support_notes) }
        : null,
      leaf_shape: ne(f.growth_habit.leaf_shape),
      leaf_texture: ne(f.growth_habit.leaf_texture),
      venation: ne(f.growth_habit.venation),
      petiole: ne(f.growth_habit.petiole),
      stem_growth_form: ne(f.growth_habit.stem_growth_form),
      diagnostic_features: f.growth_habit.diagnostic_features.filter(Boolean),
    },
    dormancy: ne(f.dormancy),
    propagation_methods: f.propagation_methods.map(m => ({ method: m.method, success_rate: m.success_rate, notes: ne(m.notes) })),
    common_pests: f.common_pests.filter(Boolean),
    common_problems: f.common_problems,
    toxicity: f.toxicity.toxic
      ? { toxic: true, severity: ne(f.toxicity.severity), affected: ne(f.toxicity.affected), description: ne(f.toxicity.description) }
      : { toxic: false },
    outdoor_care: f.outdoor_suitable ? {
      outdoor_suitable: true,
      sun_tolerance: f.outdoor_sun_tolerance,
      min_outdoor_temp_f: ni(f.outdoor_min_temp_f),
      min_outdoor_temp_c: ni(f.outdoor_min_temp_c),
      acclimation_notes: ne(f.outdoor_acclimation_notes),
      outdoor_risks: ne(f.outdoor_risks),
    } : { outdoor_suitable: false },
    cultivar_notes: ne(f.cultivar_notes),
    grower_notes: ne(f.grower_notes),
    profile_notes: ne(f.profile_notes),
  };
}

function EditorSection({ title, children }) {
  return (
    <div style={{ marginBottom: '0.65rem', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '0.3rem 0.65rem', background: 'var(--surface-recessed)', fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
        {title}
      </div>
      <div style={{ padding: '0.5rem 0.65rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem 0.75rem' }}>
        {children}
      </div>
    </div>
  );
}

function F({ label, span, children }) {
  return (
    <div style={{ gridColumn: span === 2 ? '1 / -1' : undefined }}>
      <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.1rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const FI = { className: 'form-input', style: { fontSize: '0.8rem', padding: '0.25rem 0.4rem' } };
const FT = { className: 'form-input form-textarea', style: { fontSize: '0.8rem', padding: '0.25rem 0.4rem' }, rows: 3 };
const FS = { className: 'form-input', style: { fontSize: '0.8rem', padding: '0.25rem 0.4rem' } };

function GuidedEditor({ form, setForm, review }) {
  const setTop = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setSec = (s, k, v) => setForm(f => ({ ...f, [s]: { ...f[s], [k]: v } }));
  const addProp = () => setForm(f => ({ ...f, propagation_methods: [...f.propagation_methods, { method: 'stem_cutting', success_rate: 'moderate', notes: '' }] }));
  const removeProp = i => setForm(f => ({ ...f, propagation_methods: f.propagation_methods.filter((_, j) => j !== i) }));
  const setProp = (i, k, v) => setForm(f => ({ ...f, propagation_methods: f.propagation_methods.map((m, j) => j === i ? { ...m, [k]: v } : m) }));
  const addPest = () => setForm(f => ({ ...f, common_pests: [...f.common_pests, ''] }));
  const removePest = i => setForm(f => ({ ...f, common_pests: f.common_pests.filter((_, j) => j !== i) }));
  const setPest = (i, v) => setForm(f => ({ ...f, common_pests: f.common_pests.map((p, j) => j === i ? v : p) }));
  const addProb = () => setForm(f => ({ ...f, common_problems: [...f.common_problems, { issue: '', cause: '', solution: '' }] }));
  const removeProb = i => setForm(f => ({ ...f, common_problems: f.common_problems.filter((_, j) => j !== i) }));
  const setProb = (i, k, v) => setForm(f => ({ ...f, common_problems: f.common_problems.map((p, j) => j === i ? { ...p, [k]: v } : p) }));
  const addDiag = () => setForm(f => ({ ...f, growth_habit: { ...f.growth_habit, diagnostic_features: [...f.growth_habit.diagnostic_features, ''] } }));
  const removeDiag = i => setForm(f => ({ ...f, growth_habit: { ...f.growth_habit, diagnostic_features: f.growth_habit.diagnostic_features.filter((_, j) => j !== i) } }));
  const setDiag = (i, v) => setForm(f => ({ ...f, growth_habit: { ...f.growth_habit, diagnostic_features: f.growth_habit.diagnostic_features.map((d, j) => j === i ? v : d) } }));

  const hasFlags = review?.admin_review_flags?.length > 0;
  const allReviewCategories = ['taxonomy_review','accuracy_review','schema_review','safety_review','content_review'];
  const hasAnyConcerns = hasFlags || allReviewCategories.some(k => review?.[k]?.concerns?.length || review?.[k]?.suggested_changes?.length);

  return (
    <div>
      {/* AI Review panel */}
      {review && (
        <div style={{ marginBottom: '0.75rem', padding: '0.65rem 0.75rem', background: 'var(--surface-recessed)', borderRadius: 8, border: `1px solid ${hasFlags ? 'var(--danger-border)' : 'var(--border)'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: hasAnyConcerns ? '0.5rem' : 0 }}>
            <span style={{ fontWeight: 700, fontSize: '0.8rem' }}>AI Review</span>
            {review.verdict && (
              <span style={{ fontSize: '0.74rem', fontWeight: 700, padding: '2px 7px', borderRadius: 4, border: '1px solid currentColor', ...(VERDICT_STYLE[review.verdict] || {}) }}>
                {review.verdict.replace(/_/g, ' ')}
              </span>
            )}
            {review.overall_confidence && <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>confidence: {review.overall_confidence}</span>}
            {review.priority && <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>priority: {review.priority}</span>}
          </div>
          {hasFlags && (
            <div style={{ marginBottom: '0.5rem', padding: '0.35rem 0.55rem', borderRadius: 6, background: 'var(--danger-bg)', border: '1px solid var(--danger-border)' }}>
              <div style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--danger)', marginBottom: '0.2rem' }}>Admin Flags</div>
              {review.admin_review_flags.map((f, i) => (
                <div key={i} style={{ fontSize: '0.8rem', color: 'var(--danger)' }}>Flag: {f}</div>
              ))}
            </div>
          )}
          <ReviewSection title="Taxonomy" section={review.taxonomy_review} />
          <ReviewSection title="Accuracy" section={review.accuracy_review} />
          <ReviewSection title="Schema" section={review.schema_review} />
          <ReviewSection title="Safety" section={review.safety_review} />
          <ReviewSection title="Content" section={review.content_review} />
          {!hasAnyConcerns && <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--green, #3a7d44)' }}>No concerns flagged.</p>}
        </div>
      )}

      {/* General */}
      <EditorSection title="General">
        <F label="Care difficulty">
          <select value={form.care_difficulty} onChange={e => setTop('care_difficulty', e.target.value)} {...FS}>
            {['beginner','intermediate','advanced'].map(v => <option key={v}>{v}</option>)}
          </select>
        </F>
        <F label="Growth rate">
          <select value={form.growth_rate} onChange={e => setTop('growth_rate', e.target.value)} {...FS}>
            {['slow','moderate','fast'].map(v => <option key={v}>{v}</option>)}
          </select>
        </F>
        <F label="Distinguishing notes" span={2}>
          <textarea value={form.distinguishing_notes} onChange={e => setTop('distinguishing_notes', e.target.value)} {...FT} />
        </F>
      </EditorSection>

      {/* Light */}
      <EditorSection title="Light">
        <F label="Level">
          <select value={form.light.level} onChange={e => setSec('light', 'level', e.target.value)} {...FS}>
            {['low','low-medium','medium','medium-bright','bright indirect','direct'].map(v => <option key={v}>{v}</option>)}
          </select>
        </F>
        <F label="Lux range">
          <input value={form.light.lux} onChange={e => setSec('light', 'lux', e.target.value)} placeholder="e.g. 1,000–3,000 lux" {...FI} />
        </F>
        <F label="Description" span={2}>
          <textarea value={form.light.description} onChange={e => setSec('light', 'description', e.target.value)} {...FT} />
        </F>
      </EditorSection>

      {/* Water */}
      <EditorSection title="Water">
        <F label="Frequency">
          <input value={form.water.frequency} onChange={e => setSec('water', 'frequency', e.target.value)} {...FI} />
        </F>
        <F label="Moisture preference">
          <select value={form.water.dry_preference} onChange={e => setSec('water', 'dry_preference', e.target.value)} {...FS}>
            {['dry_between','slightly_moist','evenly_moist','wet_tolerant'].map(v => <option key={v}>{v}</option>)}
          </select>
        </F>
        <F label="Description" span={2}>
          <textarea value={form.water.description} onChange={e => setSec('water', 'description', e.target.value)} {...FT} />
        </F>
      </EditorSection>

      {/* Humidity & Temperature */}
      <EditorSection title="Humidity & Temperature">
        <F label="Humidity min %">
          <input type="number" value={form.humidity.min_percent} onChange={e => setSec('humidity', 'min_percent', e.target.value)} placeholder="e.g. 50" {...FI} />
        </F>
        <F label="Humidity ideal range">
          <input value={form.humidity.ideal_range} onChange={e => setSec('humidity', 'ideal_range', e.target.value)} placeholder="e.g. 60–80%" {...FI} />
        </F>
        <F label="Humidity description" span={2}>
          <textarea value={form.humidity.description} onChange={e => setSec('humidity', 'description', e.target.value)} {...FT} rows={2} />
        </F>
        <F label="Min temp °F">
          <input type="number" value={form.temperature.min_f} onChange={e => setSec('temperature', 'min_f', e.target.value)} placeholder="55" {...FI} />
        </F>
        <F label="Min temp °C">
          <input type="number" value={form.temperature.min_c} onChange={e => setSec('temperature', 'min_c', e.target.value)} placeholder="13" {...FI} />
        </F>
        <F label="Max temp °F">
          <input type="number" value={form.temperature.max_f} onChange={e => setSec('temperature', 'max_f', e.target.value)} placeholder="95" {...FI} />
        </F>
        <F label="Max temp °C">
          <input type="number" value={form.temperature.max_c} onChange={e => setSec('temperature', 'max_c', e.target.value)} placeholder="35" {...FI} />
        </F>
        <F label="Temperature notes" span={2}>
          <textarea value={form.temperature.description} onChange={e => setSec('temperature', 'description', e.target.value)} {...FT} rows={2} />
        </F>
      </EditorSection>

      {/* Soil */}
      <EditorSection title="Soil">
        <F label="Drainage preference">
          <select value={form.soil.drainage_preference} onChange={e => setSec('soil', 'drainage_preference', e.target.value)} {...FS}>
            {['fast_draining','well_draining','moisture_retentive'].map(v => <option key={v}>{v}</option>)}
          </select>
        </F>
        <F label="Epiphytic">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', marginTop: '0.25rem' }}>
            <input type="checkbox" checked={form.soil.epiphytic} onChange={e => setSec('soil', 'epiphytic', e.target.checked)} />
            Yes
          </label>
        </F>
        <F label="Description" span={2}>
          <textarea value={form.soil.description} onChange={e => setSec('soil', 'description', e.target.value)} {...FT} />
        </F>
      </EditorSection>

      {/* Fertilization */}
      <EditorSection title="Fertilization">
        <F label="Frequency">
          <input value={form.fertilization.frequency} onChange={e => setSec('fertilization', 'frequency', e.target.value)} {...FI} />
        </F>
        <F label="Sensitivity">
          <select value={form.fertilization.sensitivity} onChange={e => setSec('fertilization', 'sensitivity', e.target.value)} {...FS}>
            {['low','moderate','high'].map(v => <option key={v}>{v}</option>)}
          </select>
        </F>
        <F label="Notes" span={2}>
          <textarea value={form.fertilization.notes} onChange={e => setSec('fertilization', 'notes', e.target.value)} {...FT} />
        </F>
      </EditorSection>

      {/* Growth Habit */}
      <EditorSection title="Growth Habit">
        <F label="Type (select all that apply)" span={2}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.75rem', marginTop: '0.1rem' }}>
            {['climbing_vine','trailing_vine','terrestrial_crawler','self_heading','rosette','upright'].map(v => (
              <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem' }}>
                <input type="checkbox"
                  checked={form.growth_habit.types.includes(v)}
                  onChange={e => {
                    const next = e.target.checked
                      ? [...form.growth_habit.types, v]
                      : form.growth_habit.types.filter(t => t !== v);
                    setSec('growth_habit', 'types', next);
                  }}
                />
                {v.replace(/_/g, ' ')}
              </label>
            ))}
          </div>
        </F>
        <F label="Mature size (indoor)" span={2}>
          <input value={form.growth_habit.mature_size_indoor} onChange={e => setSec('growth_habit', 'mature_size_indoor', e.target.value)} {...FI} />
        </F>
        <F label="Support (benefits_from)">
          <select value={form.growth_habit.support_type} onChange={e => setSec('growth_habit', 'support_type', e.target.value)} {...FS}>
            <option value="">none / not needed</option>
            {['moss_pole','trellis','stake','plank'].map(v => <option key={v}>{v}</option>)}
          </select>
        </F>
        <F label="Support notes">
          <input value={form.growth_habit.support_notes} onChange={e => setSec('growth_habit', 'support_notes', e.target.value)} {...FI} />
        </F>
        <F label="Leaf shape">
          <input value={form.growth_habit.leaf_shape} onChange={e => setSec('growth_habit', 'leaf_shape', e.target.value)} placeholder="e.g. cordate to sagittate, shallowly lobed at maturity" {...FI} />
        </F>
        <F label="Leaf texture">
          <input value={form.growth_habit.leaf_texture} onChange={e => setSec('growth_habit', 'leaf_texture', e.target.value)} placeholder="e.g. coriaceous, glossy, lightly bullate" {...FI} />
        </F>
        <F label="Venation">
          <input value={form.growth_habit.venation} onChange={e => setSec('growth_habit', 'venation', e.target.value)} placeholder="e.g. impressed secondary veins, pale midrib" {...FI} />
        </F>
        <F label="Petiole">
          <input value={form.growth_habit.petiole} onChange={e => setSec('growth_habit', 'petiole', e.target.value)} placeholder="e.g. terete petioles with reddish scaling" {...FI} />
        </F>
        <F label="Stem growth form" span={2}>
          <input value={form.growth_habit.stem_growth_form} onChange={e => setSec('growth_habit', 'stem_growth_form', e.target.value)} placeholder="e.g. short-jointed climber, creeping rhizome, self-heading stem" {...FI} />
        </F>
        <F label="Diagnostic features" span={2}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            {form.growth_habit.diagnostic_features.map((feature, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                <input value={feature} onChange={e => setDiag(i, e.target.value)} placeholder="Short diagnostic feature" {...FI} style={{ ...FI.style, flex: 1 }} />
                <button type="button" onClick={() => removeDiag(i)} style={{ fontSize: '0.75rem', color: 'var(--danger)', border: 'none', background: 'none', cursor: 'pointer' }} aria-label="Remove" title="Remove">✕</button>
              </div>
            ))}
            <button type="button" className="settings-lookup-action" onClick={addDiag} style={{ fontSize: '0.78rem', alignSelf: 'flex-start' }}>+ Add feature</button>
          </div>
        </F>
      </EditorSection>

      {/* Propagation */}
      <EditorSection title="Propagation">
        {form.propagation_methods.map((m, i) => (
          <div key={i} style={{ gridColumn: '1 / -1', padding: '0.4rem 0.5rem', background: 'var(--surface-recessed)', borderRadius: 6, border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.3rem', alignItems: 'center' }}>
              <select value={m.method} onChange={e => setProp(i, 'method', e.target.value)} {...FS} style={{ ...FS.style, flex: 1 }}>
                {['stem_cutting','wet_stick','division','rhizome_division','air_layering','seed','offset','corm','leaf_cutting','bulbil'].map(v => <option key={v}>{v}</option>)}
              </select>
              <select value={m.success_rate} onChange={e => setProp(i, 'success_rate', e.target.value)} {...FS} style={{ ...FS.style, width: 110 }}>
                {['high','moderate','low'].map(v => <option key={v}>{v}</option>)}
              </select>
              <button type="button" onClick={() => removeProp(i)} style={{ fontSize: '0.75rem', color: 'var(--danger)', border: 'none', background: 'none', cursor: 'pointer', padding: '0 0.2rem' }} aria-label="Remove" title="Remove">✕</button>
            </div>
            <textarea value={m.notes} onChange={e => setProp(i, 'notes', e.target.value)} placeholder="Notes" {...FT} rows={2} style={{ ...FT.style, width: '100%' }} />
          </div>
        ))}
        <div style={{ gridColumn: '1 / -1' }}>
          <button type="button" className="settings-lookup-action" onClick={addProp} style={{ fontSize: '0.78rem' }}>+ Add method</button>
        </div>
      </EditorSection>

      {/* Pests */}
      <EditorSection title="Common Pests">
        {form.common_pests.map((p, i) => (
          <div key={i} style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
            <input value={p} onChange={e => setPest(i, e.target.value)} {...FI} style={{ ...FI.style, flex: 1 }} />
            <button type="button" onClick={() => removePest(i)} style={{ fontSize: '0.75rem', color: 'var(--danger)', border: 'none', background: 'none', cursor: 'pointer' }} aria-label="Remove" title="Remove">✕</button>
          </div>
        ))}
        <div style={{ gridColumn: '1 / -1' }}>
          <button type="button" className="settings-lookup-action" onClick={addPest} style={{ fontSize: '0.78rem' }}>+ Add pest</button>
        </div>
      </EditorSection>

      {/* Problems */}
      <EditorSection title="Common Problems">
        {form.common_problems.map((pr, i) => (
          <div key={i} style={{ gridColumn: '1 / -1', padding: '0.4rem 0.5rem', background: 'var(--surface-recessed)', borderRadius: 6, border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.3rem' }}>
              <input value={pr.issue} onChange={e => setProb(i, 'issue', e.target.value)} placeholder="Issue" {...FI} style={{ ...FI.style, flex: 1 }} />
              <button type="button" onClick={() => removeProb(i)} style={{ fontSize: '0.75rem', color: 'var(--danger)', border: 'none', background: 'none', cursor: 'pointer' }} aria-label="Remove" title="Remove">✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem' }}>
              <textarea value={pr.cause} onChange={e => setProb(i, 'cause', e.target.value)} placeholder="Cause" {...FT} rows={2} />
              <textarea value={pr.solution} onChange={e => setProb(i, 'solution', e.target.value)} placeholder="Solution" {...FT} rows={2} />
            </div>
          </div>
        ))}
        <div style={{ gridColumn: '1 / -1' }}>
          <button type="button" className="settings-lookup-action" onClick={addProb} style={{ fontSize: '0.78rem' }}>+ Add problem</button>
        </div>
      </EditorSection>

      {/* Toxicity */}
      <EditorSection title="Toxicity">
        <F label="Toxic" span={2}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem' }}>
            <input type="checkbox" checked={form.toxicity.toxic} onChange={e => setSec('toxicity', 'toxic', e.target.checked)} />
            Toxic to humans or pets
          </label>
        </F>
        {form.toxicity.toxic && <>
          <F label="Severity">
            <select value={form.toxicity.severity} onChange={e => setSec('toxicity', 'severity', e.target.value)} {...FS}>
              <option value="">—</option>
              {['mild','moderate','severe'].map(v => <option key={v}>{v}</option>)}
            </select>
          </F>
          <F label="Affected">
            <select value={form.toxicity.affected} onChange={e => setSec('toxicity', 'affected', e.target.value)} {...FS}>
              <option value="">—</option>
              {['pets','children','both'].map(v => <option key={v}>{v}</option>)}
            </select>
          </F>
          <F label="Description" span={2}>
            <textarea value={form.toxicity.description} onChange={e => setSec('toxicity', 'description', e.target.value)} {...FT} />
          </F>
        </>}
      </EditorSection>

      {/* Dormancy */}
      {(form.dormancy || true) && (
        <EditorSection title="Dormancy">
          <F label="Dormancy notes" span={2}>
            <textarea value={form.dormancy} onChange={e => setTop('dormancy', e.target.value)} placeholder="Null / not applicable, or describe dormancy behavior and handling" {...FT} />
          </F>
        </EditorSection>
      )}

      {/* Outdoor Care */}
      <EditorSection title="Outdoor Care">
        <F label="Outdoor suitable" span={2}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem' }}>
            <input type="checkbox" checked={form.outdoor_suitable} onChange={e => setTop('outdoor_suitable', e.target.checked)} />
            Suitable for outdoor growing
          </label>
        </F>
        {form.outdoor_suitable && <>
          <F label="Sun tolerance">
            <select value={form.outdoor_sun_tolerance} onChange={e => setTop('outdoor_sun_tolerance', e.target.value)} {...FS}>
              {['none','morning_only','filtered','full_acclimated'].map(v => <option key={v}>{v}</option>)}
            </select>
          </F>
          <F label="Min outdoor temp °F">
            <input type="number" value={form.outdoor_min_temp_f} onChange={e => setTop('outdoor_min_temp_f', e.target.value)} {...FI} />
          </F>
          <F label="Min outdoor temp °C">
            <input type="number" value={form.outdoor_min_temp_c} onChange={e => setTop('outdoor_min_temp_c', e.target.value)} {...FI} />
          </F>
          <F label="Acclimation notes" span={2}>
            <textarea value={form.outdoor_acclimation_notes} onChange={e => setTop('outdoor_acclimation_notes', e.target.value)} {...FT} rows={2} />
          </F>
          <F label="Outdoor risks" span={2}>
            <textarea value={form.outdoor_risks} onChange={e => setTop('outdoor_risks', e.target.value)} {...FT} rows={2} />
          </F>
        </>}
        {!form.outdoor_suitable && (
          <F label="Acclimation notes (why not suitable)" span={2}>
            <textarea value={form.outdoor_acclimation_notes} onChange={e => setTop('outdoor_acclimation_notes', e.target.value)} {...FT} rows={2} />
          </F>
        )}
      </EditorSection>

      {/* Notes */}
      <EditorSection title="Notes">
        {form.common_names?.length > 0 && (
          <F label="Common names" span={2}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text)', padding: '0.25rem 0' }}>
              {form.common_names.join(', ')}
            </div>
            <div style={{ fontSize: '0.67rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>Read-only — edit in raw JSON if needed</div>
          </F>
        )}
        <F label="Cultivar notes" span={2}>
          <textarea value={form.cultivar_notes} onChange={e => setTop('cultivar_notes', e.target.value)} placeholder="Notable cultivars and care differences" {...FT} />
        </F>
        <F label="Grower notes" span={2}>
          <textarea value={form.grower_notes} onChange={e => setTop('grower_notes', e.target.value)} placeholder="Hobby knowledge, tips" {...FT} />
        </F>
        <F label="Profile notes (confidence flags)" span={2}>
          <textarea value={form.profile_notes} onChange={e => setTop('profile_notes', e.target.value)} placeholder="Areas of uncertainty flagged by the model" {...FT} />
        </F>
      </EditorSection>
    </div>
  );
}

// ── Batch pipeline panel ──────────────────────────────────────────────────────

function fmtIso(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function fmtElapsed(startIso, endIso) {
  if (!startIso) return null;
  try {
    const s = Math.round(((endIso ? new Date(endIso) : new Date()) - new Date(startIso)) / 1000);
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  } catch { return null; }
}

function BatchProgressBar({ done, total, errored = 0 }) {
  if (!total) return null;
  const pct = Math.round((done / total) * 100);
  return (
    <div style={{ marginTop: '0.3rem' }}>
      <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: errored ? 'var(--warning)' : 'var(--green)', borderRadius: 3, transition: 'width 0.4s' }} />
      </div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
        {done}/{total} ({pct}%){errored > 0 ? ` · ${errored} errors` : ''}
      </div>
    </div>
  );
}

function inferQueueProfileType(species) {
  const clean = (species || '').trim().toLowerCase();
  if (!clean || ['sp', 'sp.', 'spp', 'spp.', 'unknown', 'unk'].includes(clean)) return 'genus';
  if (clean.includes('×')) return 'hybrid';
  if (/^[a-z][a-z-]+\s+[a-z][a-z- ]+$/.test(clean)) return 'trade_name';
  return 'species';
}

function commonCandidateKey(candidate) {
  return `${candidate.genus}::${candidate.species}`;
}

const JOB_STATUS_STYLE = {
  requested: { background: '#fff8ec', color: '#8a5b00', border: '1px solid #f0d899' },
  queued: { background: '#eef6ff', color: '#1f5f9c', border: '1px solid #cfe3ff' },
  rejected: { background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger-border)' },
  completed: { background: '#f0f7f1', color: 'var(--green, #3a7d44)', border: '1px solid #c3dfc9' },
};

const COMMON_CANDIDATE_REASON_LABEL = {
  new: 'new',
  profile_rejected: 'profile rejected',
  profile_stale: 'profile stale',
  job_completed: 'retry available',
  job_rejected: 'job rejected',
};

const MANUAL_PROFILE_TYPE_OPTIONS = [
  { value: 'species', label: 'Species', detail: 'Standard genus + species request' },
  { value: 'genus', label: 'Genus', detail: 'Creates a general genus-level profile' },
  { value: 'hybrid', label: 'Hybrid', detail: 'Use when the identity includes × or an intergeneric hybrid' },
  { value: 'trade_name', label: 'Trade Name', detail: 'For marketed plants where the trade name is the identity' },
];

function manualIdentityFieldConfig(profileType) {
  if (profileType === 'genus') {
    return {
      key: null,
      label: null,
      placeholder: null,
      helper: 'This will queue a genus-level care profile with species set to sp.',
    };
  }
  if (profileType === 'trade_name') {
    return {
      key: null,
      label: null,
      placeholder: null,
      helper: 'Trade-name profiles store the botanical basis plus the market name, such as micholitziana Frydek or sp. Red Flag.',
    };
  }
  if (profileType === 'hybrid') {
    return {
      key: 'species',
      label: 'Hybrid identity',
      placeholder: 'squamiferum × pedatum or portora',
      helper: 'Use × in the species or genus identity for hybrid requests.',
    };
  }
  return {
    key: 'species',
    label: 'Species',
    placeholder: 'gloriosum',
    helper: 'Use the botanical species epithet only.',
  };
}

function QueueBadge({ label, style }) {
  return (
    <span style={{
      display: 'inline-block',
      fontSize: '0.7rem',
      fontWeight: 600,
      borderRadius: 999,
      padding: '2px 8px',
      ...style,
    }}>
      {label}
    </span>
  );
}

function BatchPanel({ onSaved, refreshKey = 0, selectedJobIds = [] }) {
  const [liveStatus, setLiveStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState(null);
  const [limit, setLimit] = useState('');
  const [source, setSource] = useState('jobs');
  const [genus, setGenus] = useState('');
  const [dryRun, setDryRun] = useState(false);
  const [commonList, setCommonList] = useState({ total_available: 0, shown_count: 0, candidates: [] });
  const [commonLoading, setCommonLoading] = useState(false);
  const [commonError, setCommonError] = useState(null);
  const [selectedCommonKeys, setSelectedCommonKeys] = useState([]);

  const loadLiveStatus = useCallback(async () => {
    try { setLiveStatus(await apiJson('/operator/care-batch-status')); }
    catch { setLiveStatus({ phase: 'idle' }); }
  }, []);

  const loadCommonCandidates = useCallback(async () => {
    setCommonLoading(true);
    setCommonError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(limit ? parseInt(limit, 10) : 200));
      if (genus.trim()) params.set('genus', genus.trim());
      const data = await apiJson(`/operator/care-common-list?${params.toString()}`);
      setCommonList(data);
      setSelectedCommonKeys(prev => prev.filter(key => data.candidates.some(candidate => commonCandidateKey(candidate) === key)));
    } catch (e) {
      setCommonError(e.message);
      setCommonList({ total_available: 0, shown_count: 0, candidates: [] });
    } finally {
      setCommonLoading(false);
    }
  }, [genus, limit]);

  useEffect(() => { loadLiveStatus(); }, [loadLiveStatus]);
  useEffect(() => { loadLiveStatus(); }, [refreshKey, loadLiveStatus]);
  useEffect(() => { loadCommonCandidates(); }, [loadCommonCandidates, refreshKey]);

  const batchPhase = liveStatus?.phase;

  // Auto-poll aggressively while the batch is active so progress feels live.
  useEffect(() => {
    if (!batchPhase) return;
    if (batchPhase === 'idle' || batchPhase === 'complete') return;
    const t = setInterval(loadLiveStatus, 5000);
    return () => clearInterval(t);
  }, [batchPhase, loadLiveStatus]);

  useEffect(() => {
    if (source === 'common-list' && commonList.total_available === 0 && !commonLoading) {
      setSource('jobs');
    }
  }, [source, commonList.total_available, commonLoading]);

  const run = async (command, extra = {}) => {
    setRunning(true);
    setOutput(null);
    try {
      const r = await apiJson('/operator/run-generation', {
        method: 'POST',
        body: JSON.stringify({ command, ...extra }),
      });
      setOutput({ text: r.output, ok: r.returncode === 0 });
      await loadLiveStatus();
      if (command !== 'status' && r.returncode === 0) onSaved?.();
    } catch (e) {
      setOutput({ text: `Error: ${e.message}`, ok: false });
    } finally {
      setRunning(false);
    }
  };

  if (liveStatus === null) return null;

  const phase = liveStatus.phase ?? 'idle';
  const gen = liveStatus.generation_batch;
  const rev = liveStatus.review_batch;
  const autoReview = liveStatus.review_auto || {};
  const inFlight = phase !== 'idle' && phase !== 'complete';
  const reviewStatus = rev?.status || '';
  const canSaveToDb = reviewStatus === 'completed';
  const readyForGenerate = phase === 'idle' || phase === 'complete';
  const selectedCount = selectedJobIds.length;
  const usingSelectedJobs = source === 'jobs' && selectedCount > 0;
  const selectedCommonCandidates = commonList.candidates.filter(candidate => selectedCommonKeys.includes(commonCandidateKey(candidate)));
  const commonOptionVisible = source === 'common-list' || commonList.total_available > 0;

  const toggleCommonCandidate = (candidateKey) => {
    setSelectedCommonKeys(keys => keys.includes(candidateKey) ? keys.filter(key => key !== candidateKey) : [...keys, candidateKey]);
  };

  const toggleVisibleCommonCandidates = () => {
    const visibleKeys = commonList.candidates.map(commonCandidateKey);
    const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every(key => selectedCommonKeys.includes(key));
    if (allVisibleSelected) {
      setSelectedCommonKeys(keys => keys.filter(key => !visibleKeys.includes(key)));
      return;
    }
    setSelectedCommonKeys(keys => Array.from(new Set([...keys, ...visibleKeys])));
  };

  const importCommonCandidates = async () => {
    if (selectedCommonCandidates.length === 0) return;
    setRunning(true);
    setOutput(null);
    setCommonError(null);
    try {
      const result = await apiJson('/operator/care-common-list/import', {
        method: 'POST',
        body: JSON.stringify({
          entries: selectedCommonCandidates.map(candidate => ({
            genus: candidate.genus,
            species: candidate.species,
          })),
        }),
      });
      const queuedCount = (result.created || 0) + (result.reopened || 0);
      let text = `Queued ${queuedCount} common-list entr${queuedCount === 1 ? 'y' : 'ies'} (${result.created || 0} new, ${result.reopened || 0} reopened).`;
      if (result.skipped?.length) {
        text += `\nSkipped ${result.skipped.length} entr${result.skipped.length === 1 ? 'y' : 'ies'} that were no longer actionable.`;
      }
      setOutput({ text, ok: true });
      setSelectedCommonKeys([]);
      await loadCommonCandidates();
      onSaved?.();
      setSource('jobs');
    } catch (e) {
      setCommonError(e.message);
      setOutput({ text: `Error: ${e.message}`, ok: false });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{ marginBottom: '1rem', padding: '0.65rem 0.85rem', background: 'var(--surface-recessed)', borderRadius: 8, border: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text)' }}>Batch Pipeline</span>
        {inFlight && (
          <button type="button" className="settings-lookup-action" style={{ fontSize: '0.7rem' }} disabled={running} onClick={loadLiveStatus}>
            Refresh
          </button>
        )}
      </div>

      {/* Timing timeline */}
      {liveStatus.submitted_at && (
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.5rem', lineHeight: 1.7 }}>
          <div>
            <span style={{ display: 'inline-block', width: 130 }}>Submitted:</span>
            {fmtIso(liveStatus.submitted_at)}
          </div>
          {liveStatus.generation_ended_at && (
            <div>
              <span style={{ display: 'inline-block', width: 130 }}>Generation ended:</span>
              {fmtIso(liveStatus.generation_ended_at)}
              <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>
                (+{fmtElapsed(liveStatus.submitted_at, liveStatus.generation_ended_at)})
              </span>
            </div>
          )}
          {liveStatus.review_submitted_at && (
            <div>
              <span style={{ display: 'inline-block', width: 130 }}>Review submitted:</span>
              {fmtIso(liveStatus.review_submitted_at)}
              <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>
                (+{fmtElapsed(liveStatus.submitted_at, liveStatus.review_submitted_at)})
              </span>
            </div>
          )}
          {liveStatus.saved_at ? (
            <div>
              <span style={{ display: 'inline-block', width: 130 }}>Saved:</span>
              {fmtIso(liveStatus.saved_at)}
              <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>
                (total: {fmtElapsed(liveStatus.submitted_at, liveStatus.saved_at)}
                {liveStatus.results_count > 0 ? ` · avg ${Math.round(
                  (new Date(liveStatus.saved_at) - new Date(liveStatus.submitted_at)) / 1000 / liveStatus.results_count
                )}s/species` : ''})
              </span>
            </div>
          ) : (
            <div>
              <span style={{ display: 'inline-block', width: 130 }}>Elapsed:</span>
              <span style={{ color: 'var(--green)' }}>{fmtElapsed(liveStatus.submitted_at)}</span>
            </div>
          )}
        </div>
      )}

      {/* Generation batch status */}
      {gen && (
        <div style={{ marginBottom: '0.45rem', padding: '0.35rem 0.5rem', background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, marginBottom: '0.1rem' }}>
            Generation · <code style={{ fontSize: '0.68rem', fontWeight: 400 }}>{gen.id}</code>
            <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 10, fontSize: '0.67rem', background: gen.status === 'ended' ? '#d4edda' : '#fff3cd', color: gen.status === 'ended' ? '#155724' : '#856404' }}>
              {gen.status}
            </span>
          </div>
          {gen.error
            ? <div style={{ fontSize: '0.7rem', color: 'var(--danger)' }}>{gen.error}</div>
            : <BatchProgressBar done={gen.succeeded} total={gen.total} errored={gen.errored} />
          }
        </div>
      )}

      {/* Review batch status */}
      {rev && (
        <div style={{ marginBottom: '0.45rem', padding: '0.35rem 0.5rem', background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, marginBottom: '0.1rem' }}>
            Review · <code style={{ fontSize: '0.68rem', fontWeight: 400 }}>{rev.id}</code>
            <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 10, fontSize: '0.67rem', background: rev.status === 'completed' ? '#d4edda' : '#fff3cd', color: rev.status === 'completed' ? '#155724' : '#856404' }}>
              {rev.status}
            </span>
          </div>
          {rev.error
            ? <div style={{ fontSize: '0.7rem', color: 'var(--danger)' }}>{rev.error}</div>
            : <BatchProgressBar done={rev.completed} total={rev.total} errored={rev.failed} />
          }
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.3rem' }}>
        {readyForGenerate && (
          <>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', width: '100%', marginBottom: '0.3rem' }}>
              <select value={source} onChange={e => setSource(e.target.value)} className="form-input" style={{ width: 170, fontSize: '0.8rem' }}>
                <option value="jobs">User requests (all)</option>
                {commonOptionVisible && <option value="common-list">Common list</option>}
              </select>
              <input type="number" min="1" value={limit} onChange={e => setLimit(e.target.value)}
                placeholder="limit (opt)" className="form-input" style={{ width: 120, fontSize: '0.8rem' }} />
              {source === 'common-list' && (
                <input value={genus} onChange={e => setGenus(e.target.value)}
                  placeholder="genus (opt)" className="form-input" style={{ width: 140, fontSize: '0.8rem' }} />
              )}
              {source === 'jobs' && (
                <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} /> dry run
                </label>
              )}
              {usingSelectedJobs && (
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  {selectedCount} selected request{selectedCount !== 1 ? 's' : ''} will be used
                </span>
              )}
            </div>
            {source === 'jobs' && (
              <div style={{ width: '100%', marginBottom: '0.35rem', fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
                This launches the batch pipeline for all requested jobs, or only the selected queue rows if you have checked requests below.
              </div>
            )}
            {source === 'common-list' ? (
              <button type="button" className="btn btn-primary btn-sm" disabled={running || selectedCommonCandidates.length === 0} onClick={importCommonCandidates}>
                {running ? 'Queueing…' : `Queue selected common-list entries${selectedCommonCandidates.length ? ` (${selectedCommonCandidates.length})` : ''}`}
              </button>
            ) : (
              <button type="button" className="btn btn-primary btn-sm" disabled={running}
                onClick={() => run('generate', {
                  source,
                  ...(usingSelectedJobs ? { job_ids: selectedJobIds } : {}),
                  ...(limit ? { limit: parseInt(limit) } : {}),
                  dry_run: dryRun,
                })}>
                {running
                  ? 'Submitting…'
                  : usingSelectedJobs
                    ? `Submit selected batch (${selectedCount})`
                    : 'Submit all requested batch'}
              </button>
            )}
          </>
        )}
        {(phase === 'generation' || phase === 'generation_ended') && autoReview.status === 'blocked' && (
          <button type="button" className="btn btn-primary btn-sm" disabled={running} onClick={() => run('review')}>
            {running ? 'Running…' : 'Review manually despite generation errors'}
          </button>
        )}
        {(phase === 'generation' || phase === 'generation_ended') && autoReview.status === 'error' && (
          <button type="button" className="btn btn-primary btn-sm" disabled={running} onClick={() => run('review')}>
            {running ? 'Running…' : 'Retry review submission'}
          </button>
        )}
        {(phase === 'review' || phase === 'review_ended') && (
          <button type="button" className="btn btn-primary btn-sm" disabled={running || !canSaveToDb} onClick={() => run('save')}>
            {running ? 'Saving…' : canSaveToDb ? 'Save to DB ↓' : 'Waiting for review…'}
          </button>
        )}
        {!readyForGenerate && (
          <>
            <button type="button" className="settings-lookup-action" disabled={running} onClick={() => run('status')}>
              Show status log
            </button>
            <ConfirmButton
              label="Force Reset"
              confirmLabel="Abandon this batch? This cannot be undone"
              onConfirm={() => run('reset')}
              busy={running}
              danger={true}
            />
          </>
        )}
      </div>

      {(phase === 'generation' || phase === 'generation_ended') && (
        <div style={{ width: '100%', marginTop: '0.45rem', fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
          {autoReview.status === 'blocked'
            ? 'Automatic OpenAI review submission paused because the generation batch reported errors. You can inspect the output and submit review manually if you still want to salvage successful profiles.'
            : autoReview.status === 'error'
              ? 'Automatic OpenAI review submission ran into an error. You can retry it manually from here after checking the status log.'
              : autoReview.status === 'submitting' || autoReview.status === 'submitted'
                ? 'OpenAI review submission happens automatically after a clean generation batch finishes.'
                : 'Once generation finishes cleanly, the OpenAI review batch will be submitted automatically.'}
        </div>
      )}
      {autoReview.last_error && (phase === 'generation' || phase === 'generation_ended') && autoReview.status === 'error' && (
        <div style={{ width: '100%', marginTop: '0.35rem', fontSize: '0.72rem', color: 'var(--danger)', whiteSpace: 'pre-wrap' }}>
          {autoReview.last_error}
        </div>
      )}

      {readyForGenerate && source === 'common-list' && (
        <div style={{ marginTop: '0.65rem', padding: '0.65rem 0.75rem', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.45rem' }}>
            <div>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text)' }}>Common-list seed candidates</div>
              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                Queue startup species from the curated reference list. Once queued, they move into the normal request workflow below.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                {commonList.total_available} actionable entr{commonList.total_available === 1 ? 'y' : 'ies'}
              </span>
              <button type="button" className="settings-lookup-action" onClick={loadCommonCandidates} disabled={commonLoading}>
                {commonLoading ? 'Loading…' : 'Refresh list'}
              </button>
              <button type="button" className="settings-lookup-action" onClick={toggleVisibleCommonCandidates} disabled={commonList.candidates.length === 0}>
                {commonList.candidates.length > 0 && commonList.candidates.every(candidate => selectedCommonKeys.includes(commonCandidateKey(candidate))) ? 'Clear visible' : 'Select visible'}
              </button>
            </div>
          </div>

          {commonError && <p className="settings-save-error" style={{ marginBottom: '0.5rem' }}>{commonError}</p>}

          {commonList.total_available === 0 ? (
            <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              The curated common list has already been worked through. This option will reappear if entries become actionable again.
            </p>
          ) : commonList.candidates.length === 0 ? (
            <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              No common-list candidates match the current genus filter.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', maxHeight: 260, overflow: 'auto', paddingRight: '0.1rem' }}>
              {commonList.candidates.map(candidate => {
                const key = commonCandidateKey(candidate);
                const selected = selectedCommonKeys.includes(key);
                return (
                  <label
                    key={key}
                    style={{
                      display: 'flex',
                      gap: '0.65rem',
                      alignItems: 'flex-start',
                      padding: '0.55rem 0.65rem',
                      borderRadius: 8,
                      border: selected ? '1px solid var(--green, #3a7d44)' : '1px solid var(--border)',
                      background: selected ? 'var(--green-light, #f0f7f1)' : 'var(--surface-recessed)',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleCommonCandidate(key)}
                      style={{ marginTop: '0.15rem' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.15rem' }}>
                        <span style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--text)' }}>
                          {candidate.genus} {candidate.species}
                        </span>
                        <QueueBadge label={candidate.profile_type.replace(/_/g, ' ')} style={{ background: 'var(--surface)', color: 'var(--text-muted)', border: '1px solid var(--border)' }} />
                        <QueueBadge label={COMMON_CANDIDATE_REASON_LABEL[candidate.action_reason] || candidate.action_reason} style={{ background: '#eef6ff', color: '#1f5f9c', border: '1px solid #cfe3ff' }} />
                      </div>
                      <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
                        {candidate.family || 'Family unknown'}
                        {candidate.common_name ? ` · ${candidate.common_name}` : ''}
                      </div>
                      {candidate.notes && (
                        <div style={{ marginTop: '0.2rem', fontSize: '0.77rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
                          {candidate.notes}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {output && (
        <pre style={{ margin: '0.5rem 0 0', padding: '0.45rem 0.65rem', background: output.ok ? '#f0f7f1' : 'var(--danger-bg)', border: `1px solid ${output.ok ? '#c3e6cb' : 'var(--danger-border)'}`, borderRadius: 6, fontSize: '0.71rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 260, overflow: 'auto' }}>
          {output.text}
        </pre>
      )}
    </div>
  );
}

const GUIDED_PROFILE_KEYS = new Set([
  'common_names','distinguishing_notes','care_difficulty','growth_rate','light','water','humidity',
  'temperature','soil','fertilization','growth_habit','propagation_methods','dormancy',
  'common_pests','common_problems','toxicity','outdoor_care',
  'cultivar_notes','grower_notes','profile_notes',
]);

function BrowseINaturalistPanel({ genus, species, onQueued }) {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [queuing, setQueuing] = useState({});   // photo_id → true while posting
  const [queued, setQueued] = useState({});     // photo_id → true after success

  const fetchPage = useCallback(async (pg) => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({
        taxon_name: `${genus} ${species}`,
        captive_cultivated: 'true',
        photos: 'true',
        order_by: 'votes',
        per_page: '12',
        page: String(pg),
      });
      const resp = await fetch(`https://api.inaturalist.org/v1/observations?${params}`);
      if (!resp.ok) throw new Error(`iNaturalist error: ${resp.status}`);
      const data = await resp.json();
      const extracted = [];
      for (const obs of (data.results || [])) {
        for (const op of (obs.observation_photos || [])) {
          const p = op.photo;
          if (!p?.url) continue;
          // iNaturalist URLs come as square thumbnails; swap for large
          const largeUrl = p.url.replace('/square.', '/large.').replace('square.jpg', 'large.jpg');
          const thumbUrl = p.url;
          extracted.push({
            id: p.id,
            thumb: thumbUrl,
            large: largeUrl,
            source_url: `https://www.inaturalist.org/observations/${obs.id}`,
            attribution: p.attribution || '',
            license: p.license_code || '',
          });
        }
      }
      if (pg === 1) {
        setPhotos(extracted);
      } else {
        setPhotos(prev => [...prev, ...extracted]);
      }
      setHasMore(extracted.length === 12);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [genus, species]);

  const handleOpen = () => {
    setOpen(true);
    if (photos.length === 0) fetchPage(1);
  };

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPage(next);
  };

  const handleQueue = async (photo) => {
    setQueuing(q => ({ ...q, [photo.id]: true }));
    try {
      await apiJson('/operator/reference-photos/from-inaturalist', {
        method: 'POST',
        body: JSON.stringify({
          photo_url: photo.large,
          source_url: photo.source_url,
          attribution: photo.attribution,
          license: photo.license,
          genus,
          species,
        }),
      });
      setQueued(q => ({ ...q, [photo.id]: true }));
      onQueued();
    } catch (e) {
      setErr(e.message);
    } finally {
      setQueuing(q => ({ ...q, [photo.id]: false }));
    }
  };

  const licenseStyle = {
    fontSize: '0.58rem', background: 'var(--border)', padding: '1px 4px',
    borderRadius: 3, color: 'var(--text-muted)', display: 'inline-block',
    marginBottom: 2, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };

  return (
    <div style={{ marginTop: '0.5rem' }}>
      {!open ? (
        <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '0.75rem' }} onClick={handleOpen}>
          Browse iNaturalist photos
        </button>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.6rem', background: 'var(--surface-recessed)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>
              iNaturalist · <em>{genus} {species}</em> · captive/cultivated
            </span>
            <button type="button" className="settings-lookup-action" style={{ fontSize: '0.72rem' }} onClick={() => setOpen(false)}>
              Close
            </button>
          </div>

          {err && <p style={{ fontSize: '0.75rem', color: 'var(--danger)', margin: '0 0 0.4rem' }}>{err}</p>}

          {photos.length === 0 && !loading && (
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: 0 }}>
              No cultivated observations found on iNaturalist for this species.
            </p>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
            {photos.map(photo => (
              <div key={photo.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <img
                  src={photo.thumb}
                  alt="iNaturalist observation"
                  style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)', display: 'block' }}
                  loading="lazy"
                />
                {photo.license && <span style={licenseStyle}>{photo.license}</span>}
                <button
                  type="button"
                  className={`btn btn-sm ${queued[photo.id] ? 'btn-secondary' : 'btn-primary'}`}
                  style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', width: '100%' }}
                  onClick={() => handleQueue(photo)}
                  disabled={queuing[photo.id] || queued[photo.id]}
                >
                  {queuing[photo.id] ? '…' : queued[photo.id] ? 'Queued ✓' : 'Queue'}
                </button>
              </div>
            ))}
          </div>

          {loading && <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0.4rem 0 0' }}>Loading…</p>}

          {hasMore && !loading && (
            <button type="button" className="settings-lookup-action" style={{ fontSize: '0.75rem', marginTop: '0.5rem' }} onClick={handleLoadMore}>
              Load more
            </button>
          )}

          <p style={{ fontSize: '0.65rem', color: 'var(--text-muted)', margin: '0.5rem 0 0' }}>
            Photos are from iNaturalist cultivated observations, sorted by community votes.
            Attribution and license are stored when queued. Labels are set during review.
          </p>
        </div>
      )}
    </div>
  );
}

function ProfileReferencePhotoManager({ profileId, genus, species }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState({});
  const [localMeta, setLocalMeta] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const d = await apiJson(`/operator/care-profiles/${profileId}/reference-photos`);
      setData(d);
      const meta = {};
      for (const p of [...d.assigned, ...d.available]) {
        meta[p.reference_photo_id] = { photo_part: p.photo_part || '', growth_stage: p.growth_stage || '' };
      }
      setLocalMeta(meta);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  const act = async (key, fn) => {
    setBusy(b => ({ ...b, [key]: true }));
    try {
      await fn();
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  };

  const assign = (refPhotoId) => act(`assign_${refPhotoId}`, () =>
    apiJson(`/operator/care-profiles/${profileId}/reference-photos`, {
      method: 'POST',
      body: JSON.stringify({ reference_photo_id: refPhotoId }),
    })
  );
  const remove = (refPhotoId) => act(`remove_${refPhotoId}`, () =>
    apiFetch(`/operator/care-profiles/${profileId}/reference-photos/${refPhotoId}`, { method: 'DELETE' })
  );
  const setHero = (refPhotoId, isHero) => act(`hero_${refPhotoId}`, () =>
    apiJson(`/operator/care-profiles/${profileId}/reference-photos/${refPhotoId}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_hero: isHero }),
    })
  );

  const updateMeta = (refPhotoId, field, value) => {
    setLocalMeta(m => ({ ...m, [refPhotoId]: { ...(m[refPhotoId] || {}), [field]: value } }));
    setBusy(b => ({ ...b, [`meta_${refPhotoId}`]: true }));
    apiJson(`/operator/reference-photos/${refPhotoId}`, {
      method: 'PATCH',
      body: JSON.stringify({ [field]: value || null }),
    }).finally(() => setBusy(b => ({ ...b, [`meta_${refPhotoId}`]: false })));
  };

  const thumbStyle = (isHero) => ({
    width: 80, height: 80, objectFit: 'cover', borderRadius: 4,
    border: isHero ? '2px solid var(--green)' : '2px solid var(--border)',
    display: 'block',
  });

  return (
    <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
        Reference Photos
      </div>
      {loading && <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Loading…</p>}
      {err && <p style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>{err}</p>}
      {data && (
        <>
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>Assigned</div>
            {data.assigned.length === 0 ? (
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>
                No approved reference photos yet. Photos can come from your collection (consent in lightbox) or be seeded externally.
              </p>
            ) : (
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {data.assigned.map(p => (
                  <div key={p.reference_photo_id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    <div style={{ position: 'relative' }}>
                      <img
                        src={`/reference-photos/${p.reference_photo_id}/file${p.transform_version ? `?v=${p.transform_version}` : ''}`}
                        alt={p.photo_part || 'Reference'}
                        style={thumbStyle(p.is_hero)}
                      />
                      {p.is_hero === 1 && (
                        <span style={{ position: 'absolute', top: 2, right: 2, fontSize: '0.6rem', background: 'var(--green)', color: '#fff', borderRadius: 3, padding: '1px 4px', fontWeight: 700 }}>
                          Hero
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: 80 }}>
                      <select
                        className="form-input"
                        style={{ fontSize: '0.62rem', padding: '0.1rem 0.2rem', height: 'auto' }}
                        value={localMeta[p.reference_photo_id]?.photo_part ?? p.photo_part ?? ''}
                        onChange={e => updateMeta(p.reference_photo_id, 'photo_part', e.target.value)}
                        disabled={busy[`meta_${p.reference_photo_id}`]}
                      >
                        {PHOTO_PART_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <select
                        className="form-input"
                        style={{ fontSize: '0.62rem', padding: '0.1rem 0.2rem', height: 'auto' }}
                        value={localMeta[p.reference_photo_id]?.growth_stage ?? p.growth_stage ?? ''}
                        onChange={e => updateMeta(p.reference_photo_id, 'growth_stage', e.target.value)}
                        disabled={busy[`meta_${p.reference_photo_id}`]}
                      >
                        {PHOTO_GROWTH_STAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {p.is_hero !== 1 && (
                        <button
                          type="button"
                          className="settings-lookup-action"
                          style={{ fontSize: '0.7rem', padding: 0 }}
                          onClick={() => setHero(p.reference_photo_id, true)}
                          disabled={busy[`hero_${p.reference_photo_id}`]}
                        >
                          Set hero
                        </button>
                      )}
                      {p.is_hero === 1 && (
                        <button
                          type="button"
                          className="settings-lookup-action"
                          style={{ fontSize: '0.7rem', padding: 0 }}
                          onClick={() => setHero(p.reference_photo_id, false)}
                          disabled={busy[`hero_${p.reference_photo_id}`]}
                        >
                          Clear hero
                        </button>
                      )}
                      <button
                        type="button"
                        className="settings-lookup-action"
                        style={{ fontSize: '0.7rem', padding: 0, color: 'var(--danger)' }}
                        onClick={() => remove(p.reference_photo_id)}
                        disabled={busy[`remove_${p.reference_photo_id}`]}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {data.available.length > 0 && (
            <div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>Available</div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {data.available.map(p => (
                  <div key={p.reference_photo_id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    <div style={{ position: 'relative' }}>
                      <img
                        src={`/reference-photos/${p.reference_photo_id}/file${p.transform_version ? `?v=${p.transform_version}` : ''}`}
                        alt={p.photo_part || 'Reference'}
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '2px solid var(--border)', display: 'block' }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: 80 }}>
                      <select
                        className="form-input"
                        style={{ fontSize: '0.62rem', padding: '0.1rem 0.2rem', height: 'auto' }}
                        value={localMeta[p.reference_photo_id]?.photo_part ?? p.photo_part ?? ''}
                        onChange={e => updateMeta(p.reference_photo_id, 'photo_part', e.target.value)}
                        disabled={busy[`meta_${p.reference_photo_id}`]}
                      >
                        {PHOTO_PART_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <select
                        className="form-input"
                        style={{ fontSize: '0.62rem', padding: '0.1rem 0.2rem', height: 'auto' }}
                        value={localMeta[p.reference_photo_id]?.growth_stage ?? p.growth_stage ?? ''}
                        onChange={e => updateMeta(p.reference_photo_id, 'growth_stage', e.target.value)}
                        disabled={busy[`meta_${p.reference_photo_id}`]}
                      >
                        {PHOTO_GROWTH_STAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <button
                      type="button"
                      className="settings-lookup-action"
                      style={{ fontSize: '0.7rem', padding: 0 }}
                      onClick={() => assign(p.reference_photo_id)}
                      disabled={busy[`assign_${p.reference_photo_id}`]}
                    >
                      Add to profile
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add / browse photos for this species */}
          <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--border)', paddingTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {showAddForm ? (
              <AddExternalReferencePhotoForm
                defaultGenus={genus}
                defaultSpecies={species}
                onSuccess={() => { setShowAddForm(false); load(); }}
                onCancel={() => setShowAddForm(false)}
              />
            ) : (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ fontSize: '0.75rem' }}
                onClick={() => setShowAddForm(true)}
              >
                + Add photo for this species
              </button>
            )}
            {genus && species && (
              <BrowseINaturalistPanel genus={genus} species={species} onQueued={load} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

const LICENSE_OPTIONS = [
  { value: '', label: 'Unknown / unspecified' },
  { value: 'CC0', label: 'CC0 (Public Domain)' },
  { value: 'CC-BY', label: 'CC BY' },
  { value: 'CC-BY-SA', label: 'CC BY-SA' },
  { value: 'CC-BY-NC', label: 'CC BY-NC' },
  { value: 'CC-BY-NC-SA', label: 'CC BY-NC-SA' },
  { value: 'Wikimedia Commons', label: 'Wikimedia Commons' },
  { value: 'other', label: 'Other (specify in attribution)' },
];

function parseFlickrAttributionHtml(html) {
  try {
    const doc = new DOMParser().parseFromString(html.trim(), 'text/html');
    const links = [...doc.querySelectorAll('a')];

    // Embed code format: <a data-flickr-embed="true" href="…/photos/user/id/in/…"><img src="CDN_URL"></a>
    const embedLink = links.find(a => a.getAttribute('data-flickr-embed') === 'true');
    if (embedLink) {
      const img = embedLink.querySelector('img');
      const imgSrc = img ? (img.getAttribute('src') || '') : '';
      const pageUrlRaw = embedLink.getAttribute('href') || '';
      // Strip /in/photolist-… suffix to get the canonical page URL
      const pageUrl = pageUrlRaw.replace(/\/in\/.+$/, '').replace(/\/$/, '');
      if (pageUrl.includes('flickr.com/photos/')) {
        // Prefer the direct CDN URL from <img src> so the backend downloads without oEmbed
        return { photoUrl: imgSrc || pageUrl, authorName: null, licenseText: null, isEmbed: true };
      }
    }

    // Attribution HTML format: links[0]=photo page, links[1]=author, links[2]=license
    if (links.length < 3) return null;
    const photoUrl = links[0].getAttribute('href') || '';
    const authorName = links[1].textContent.trim();
    const licenseText = links[2].textContent.trim();
    if (!photoUrl.includes('flickr.com/photos/') || !authorName) return null;
    return { photoUrl, authorName, licenseText, isEmbed: false };
  } catch {
    return null;
  }
}

function matchFlickrLicense(text) {
  const t = (text || '').toUpperCase();
  if (t.includes('CC BY-NC-SA')) return 'CC-BY-NC-SA';
  if (t.includes('CC BY-NC'))    return 'CC-BY-NC';
  if (t.includes('CC BY-SA'))    return 'CC-BY-SA';
  if (t.includes('CC BY'))       return 'CC-BY';
  if (t.includes('CC0') || t.includes('PUBLIC DOMAIN')) return 'CC0';
  return '';
}

function AddExternalReferencePhotoForm({ defaultGenus, defaultSpecies, onSuccess, onCancel }) {
  const [sourceMode, setSourceMode] = useState('url'); // 'url' | 'file'
  const [genus, setGenus] = useState(defaultGenus || '');
  const [species, setSpecies] = useState(defaultSpecies || '');
  const [photoPart, setPhotoPart] = useState('');
  const [growthStage, setGrowthStage] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [file, setFile] = useState(null);
  const [attribution, setAttribution] = useState('');
  const [photoLicense, setPhotoLicense] = useState('');
  const [notes, setNotes] = useState('');
  const [initialStatus, setInitialStatus] = useState('pending');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [flickrHtml, setFlickrHtml] = useState('');
  const [parsedFlickr, setParsedFlickr] = useState(null);

  const locked = !!(defaultGenus && defaultSpecies);

  const handleFlickrHtmlChange = (html) => {
    setFlickrHtml(html);
    if (!html.trim()) { setParsedFlickr(null); return; }
    const parsed = parseFlickrAttributionHtml(html);
    if (parsed) {
      setParsedFlickr(parsed);
      setSourceUrl(parsed.photoUrl);
      if (parsed.authorName) setAttribution(`Photo by ${parsed.authorName}`);
      if (parsed.licenseText) {
        const licVal = matchFlickrLicense(parsed.licenseText);
        if (licVal) setPhotoLicense(licVal);
      }
    } else {
      setParsedFlickr(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!genus.trim() || !species.trim()) { setErr('Genus and species are required.'); return; }
    if (sourceMode === 'url' && !sourceUrl.trim()) { setErr('Source URL is required.'); return; }
    if (sourceMode === 'file' && !file) { setErr('Please choose a file.'); return; }
    setErr(null);
    setSubmitting(true);
    try {
      let result;
      if (sourceMode === 'file') {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('genus', genus.trim());
        fd.append('species', species.trim());
        if (photoPart) fd.append('photo_part', photoPart);
        if (growthStage) fd.append('growth_stage', growthStage);
        if (attribution) fd.append('attribution', attribution.trim());
        if (photoLicense) fd.append('photo_license', photoLicense);
        if (notes) fd.append('notes', notes.trim());
        fd.append('status', initialStatus);
        const resp = await apiFetch('/operator/reference-photos/upload', { method: 'POST', body: fd });
        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(text || `HTTP ${resp.status}`);
        }
        result = await resp.json();
      } else {
        // Download and store locally rather than saving a fragile redirect URL
        result = await apiJson('/operator/reference-photos/from-inaturalist', {
          method: 'POST',
          body: JSON.stringify({
            genus: genus.trim(),
            species: species.trim(),
            photo_part: photoPart || null,
            growth_stage: growthStage || null,
            photo_url: sourceUrl.trim(),
            source_url: sourceUrl.trim(),
            attribution: attribution.trim() || null,
            license: photoLicense || null,
            notes: notes.trim() || null,
            status: initialStatus,
          }),
        });
      }
      onSuccess(result);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { fontSize: '0.78rem', padding: '0.25rem 0.4rem' };
  const labelStyle = { fontSize: '0.72rem', color: 'var(--text-muted)', display: 'block', marginBottom: 2 };
  const fieldStyle = { marginBottom: '0.55rem' };
  const tabStyle = (active) => ({
    fontSize: '0.75rem', padding: '0.2rem 0.65rem', borderRadius: 99, cursor: 'pointer',
    border: '1px solid', borderColor: active ? 'var(--green)' : 'var(--border)',
    background: active ? 'var(--green)' : 'transparent',
    color: active ? '#fff' : 'var(--text-muted)', fontWeight: active ? 600 : 400,
  });

  return (
    <form
      onSubmit={handleSubmit}
      style={{ background: 'var(--surface-recessed)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '0.75rem' }}
    >
      <div style={{ fontWeight: 600, fontSize: '0.78rem', marginBottom: '0.6rem', color: 'var(--text)' }}>
        Add External Reference Photo
      </div>

      {/* Species */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.55rem' }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Genus *</label>
          <input className="form-input" style={inputStyle} value={genus} disabled={locked}
            onChange={e => setGenus(e.target.value)} placeholder="e.g. Monstera" required />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Species *</label>
          <input className="form-input" style={inputStyle} value={species} disabled={locked}
            onChange={e => setSpecies(e.target.value)} placeholder="e.g. deliciosa" required />
        </div>
      </div>

      {/* Photo part */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.55rem', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 150px' }}>
          <label style={labelStyle}>Photo label</label>
          <select className="form-input" style={inputStyle} value={photoPart} onChange={e => setPhotoPart(e.target.value)}>
            {PHOTO_PART_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <label style={labelStyle}>Foliage stage</label>
          <select className="form-input" style={inputStyle} value={growthStage} onChange={e => setGrowthStage(e.target.value)}>
            {PHOTO_GROWTH_STAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* Source toggle */}
      <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '0.5rem' }}>
        <button type="button" style={tabStyle(sourceMode === 'url')} onClick={() => setSourceMode('url')}>URL</button>
        <button type="button" style={tabStyle(sourceMode === 'file')} onClick={() => setSourceMode('file')}>File upload</button>
      </div>

      {sourceMode === 'url' ? (
        <>
          <div style={fieldStyle}>
            <label style={labelStyle}>Flickr — paste embed code or attribution HTML to auto-fill</label>
            <textarea
              className="form-input"
              style={{ ...inputStyle, width: '100%', fontFamily: 'monospace', fontSize: '0.68rem', minHeight: '3.5rem', resize: 'vertical' }}
              value={flickrHtml}
              onChange={e => handleFlickrHtmlChange(e.target.value)}
              placeholder={'Paste the Flickr embed code or attribution HTML here (optional).\nEmbed code: Flickr → Share → Embed  |  Attribution HTML: Flickr → Share → HTML'}
            />
            {flickrHtml.trim() && (
              parsedFlickr
                ? parsedFlickr.isEmbed
                  ? <p style={{ fontSize: '0.67rem', color: 'var(--green, #3a8a4a)', margin: '0.15rem 0 0' }}>
                      ✓ Image URL extracted from embed code — fill in attribution and license below.
                    </p>
                  : <p style={{ fontSize: '0.67rem', color: 'var(--green, #3a8a4a)', margin: '0.15rem 0 0' }}>
                      ✓ {parsedFlickr.authorName} · {parsedFlickr.licenseText}
                    </p>
                : <p style={{ fontSize: '0.67rem', color: 'var(--danger)', margin: '0.15rem 0 0' }}>
                    Could not parse — paste the Flickr embed code or attribution HTML.
                  </p>
            )}
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Direct image URL *</label>
            <input className="form-input" style={{ ...inputStyle, width: '100%' }} value={sourceUrl}
              onChange={e => setSourceUrl(e.target.value)} placeholder="https://…" type="url" />
            <p style={{ fontSize: '0.67rem', color: 'var(--text-muted)', margin: '0.2rem 0 0' }}>
              Paste a Flickr page URL or a direct CDN image URL — the photo will be downloaded and stored locally.
              Flickr page URLs are resolved automatically; you can also right-click a photo → Open image in new tab.
            </p>
          </div>
        </>
      ) : (
        <div style={fieldStyle}>
          <label style={labelStyle}>Image file *</label>
          <input type="file" accept="image/*"
            style={{ fontSize: '0.78rem', display: 'block' }}
            onChange={e => setFile(e.target.files[0] || null)} />
        </div>
      )}

      {/* Attribution + License */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.55rem' }}>
        <div style={{ flex: 2 }}>
          <label style={labelStyle}>Attribution</label>
          <input className="form-input" style={inputStyle} value={attribution}
            onChange={e => setAttribution(e.target.value)} placeholder="Author / source" />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>License</label>
          <select className="form-input" style={inputStyle} value={photoLicense} onChange={e => setPhotoLicense(e.target.value)}>
            {LICENSE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* Notes */}
      <div style={fieldStyle}>
        <label style={labelStyle}>Notes</label>
        <input className="form-input" style={{ ...inputStyle, width: '100%' }} value={notes}
          onChange={e => setNotes(e.target.value)} placeholder="Optional context for reviewers" />
      </div>

      {/* Initial status */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.6rem', alignItems: 'center' }}>
        <span style={labelStyle}>Queue as:</span>
        {['pending', 'approved'].map(s => (
          <label key={s} style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="radio" name="initialStatus" value={s} checked={initialStatus === s} onChange={() => setInitialStatus(s)} />
            {s === 'pending' ? 'Pending review' : 'Approved immediately'}
          </label>
        ))}
      </div>

      {err && <p style={{ fontSize: '0.75rem', color: 'var(--danger)', margin: '0 0 0.4rem' }}>{err}</p>}

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button className="btn btn-primary btn-sm" type="submit" disabled={submitting}>
          {submitting ? 'Adding…' : 'Add photo'}
        </button>
        {onCancel && (
          <button className="btn btn-secondary btn-sm" type="button" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

const CROP_ASPECTS = [
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 3 / 4 },
];

function RefPhotoLightbox({ photo, onClose, onTransformed }) {
  const [cropMode, setCropMode] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(4 / 3);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Local version tracks the transform_version for this session; seed from prop
  const [version, setVersion] = useState(photo.transform_version ?? 0);

  const imgSrc = `/operator/reference-photos/${photo.id}/file${version ? `?v=${version}` : ''}`;

  const rotate = async (direction) => {
    setBusy(true);
    setErr(null);
    try {
      const res = await apiJson(`/operator/reference-photos/${photo.id}/rotate?direction=${direction}`, { method: 'POST' });
      setVersion(res.transform_version);
      onTransformed(photo.id, res.transform_version);
      setCropMode(false);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveCrop = async () => {
    if (!croppedAreaPixels) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await apiJson(`/operator/reference-photos/${photo.id}/crop`, {
        method: 'POST',
        body: JSON.stringify(croppedAreaPixels),
      });
      setVersion(res.transform_version);
      onTransformed(photo.id, res.transform_version);
      setCropMode(false);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="ref-photo-lightbox-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="ref-photo-lightbox">
        <div className="ref-photo-lightbox-header">
          <span className="ref-photo-lightbox-title">
            <em>{photo.genus} {photo.species}</em>
            {photo.photo_part && <> · {photo.photo_part}</>}
            {photo.growth_stage && <> · {photoGrowthStageLabel(photo.growth_stage)}</>}
          </span>
          <button type="button" className="ref-photo-lightbox-close" onClick={onClose} aria-label="Close" title="Close">×</button>
        </div>

        <div className="ref-photo-lightbox-img-wrap">
          {cropMode ? (
            <Cropper
              key={version}
              image={imgSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
              style={{ containerStyle: { borderRadius: 0 } }}
            />
          ) : (
            <img src={imgSrc} alt={photo.photo_part || 'Reference'} className="ref-photo-lightbox-img" />
          )}
        </div>

        <div className="ref-photo-lightbox-toolbar">
          {!cropMode ? (
            <>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => rotate('ccw')} disabled={busy}>
                <Icon name="rotateCcw" size={14} /> CCW
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => rotate('cw')} disabled={busy}>
                <Icon name="rotateCw" size={14} /> CW
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setCropMode(true); setCroppedAreaPixels(null); }} disabled={busy}>
                Crop
              </button>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                {CROP_ASPECTS.map(a => (
                  <button
                    key={a.label}
                    type="button"
                    className={`btn btn-sm ${aspect === a.value ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setAspect(a.value); setCrop({ x: 0, y: 0 }); setZoom(1); }}
                    disabled={busy}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
              <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.55)', flexShrink: 0 }}>
                drag · scroll to zoom
              </span>
              <div style={{ display: 'flex', gap: '0.4rem', marginLeft: 'auto' }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCropMode(false)} disabled={busy}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary btn-sm" onClick={saveCrop} disabled={busy || !croppedAreaPixels}>
                  {busy ? '…' : 'Save crop'}
                </button>
              </div>
            </>
          )}
          {err && <span style={{ color: 'var(--danger)', fontSize: '0.72rem', marginLeft: 'auto' }}>{err}</span>}
        </div>
      </div>
    </div>,
    document.body
  );
}


function ReferencePhotoQueueSection() {
  const [tab, setTab] = useState('pending');
  const [q, setQ] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [photos, setPhotos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState({});
  const [localParts, setLocalParts] = useState({});
  const [localStages, setLocalStages] = useState({});
  const [pendingCount, setPendingCount] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const onTransformed = useCallback((photoId, newVersion) => {
    setPhotos(prev => prev
      ? prev.map(p => p.id === photoId ? { ...p, transform_version: newVersion } : p)
      : prev
    );
    setLightboxPhoto(prev => prev?.id === photoId ? { ...prev, transform_version: newVersion } : prev);
  }, []);

  const buildUrl = useCallback((status, overrides = {}) => {
    const params = new URLSearchParams({ status, sort_by: sortBy, sort_dir: sortDir, ...overrides });
    if (q.trim()) params.set('q', q.trim());
    if (sourceFilter) params.set('source_type', sourceFilter);
    return `/operator/reference-photo-queue?${params}`;
  }, [sortBy, sortDir, q, sourceFilter]);

  // Main data fetch — reruns whenever any filter/sort/tab/reloadKey changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    apiJson(buildUrl(tab))
      .then(d => {
        if (cancelled) return;
        setPhotos(d.photos);
        const parts = {};
        const stages = {};
        for (const p of d.photos) {
          parts[p.id] = p.photo_part || '';
          stages[p.id] = p.growth_stage || '';
        }
        setLocalParts(parts);
        setLocalStages(stages);
        if (tab === 'pending') setPendingCount(d.photos.length);
      })
      .catch(e => { if (!cancelled) setErr(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab, buildUrl, reloadKey]);

  // Keep pending badge current when viewing other tabs
  useEffect(() => {
    if (tab === 'pending') return;
    apiJson('/operator/reference-photo-queue?status=pending')
      .then(d => setPendingCount(d.photos.length))
      .catch(() => {});
  }, [tab, reloadKey]);

  const triggerReload = () => setReloadKey(k => k + 1);

  const act = async (key, fn) => {
    setBusy(b => ({ ...b, [key]: true }));
    try {
      await fn();
      triggerReload();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  };

  const approve  = (id) => act(`approve_${id}`,  () => apiJson(`/operator/reference-photos/${id}/approve`, { method: 'POST' }));
  const reject   = (id) => act(`reject_${id}`,   () => apiJson(`/operator/reference-photos/${id}/reject`,  { method: 'POST' }));
  const requeue  = (id) => act(`requeue_${id}`,  () => apiJson(`/operator/reference-photos/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'pending' }) }));
  const remove   = (id) => act(`delete_${id}`,   () => apiFetch(`/operator/reference-photos/${id}`, { method: 'DELETE' }));

  const updatePart = (id, value) => {
    setLocalParts(p => ({ ...p, [id]: value }));
    act(`part_${id}`, () =>
      apiJson(`/operator/reference-photos/${id}`, { method: 'PATCH', body: JSON.stringify({ photo_part: value || null }) })
    );
  };

  const updateGrowthStage = (id, value) => {
    setLocalStages(p => ({ ...p, [id]: value }));
    act(`stage_${id}`, () =>
      apiJson(`/operator/reference-photos/${id}`, { method: 'PATCH', body: JSON.stringify({ growth_stage: value || null }) })
    );
  };

  const TABS = [
    { key: 'pending',  label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
  ];

  const tabBtnStyle = (key) => ({
    fontSize: '0.78rem', fontWeight: key === tab ? 700 : 400,
    padding: '0.25rem 0.75rem', borderRadius: 99, cursor: 'pointer',
    border: '1px solid',
    borderColor: key === tab ? 'var(--text-muted)' : 'var(--border)',
    background: key === tab ? 'var(--border)' : 'transparent',
    color: key === tab ? 'var(--text)' : 'var(--text-muted)',
  });

  return (
    <Accordion title="Reference Photos" defaultOpen={pendingCount > 0} badge={pendingCount != null ? pendingCount : null}>
      {/* Add external photo */}
      {showAddForm ? (
        <AddExternalReferencePhotoForm
          onSuccess={() => { setShowAddForm(false); triggerReload(); }}
          onCancel={() => setShowAddForm(false)}
        />
      ) : (
        <div style={{ marginBottom: '0.75rem' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            style={{ fontSize: '0.75rem' }}
            onClick={() => setShowAddForm(true)}
          >
            + Add external photo
          </button>
        </div>
      )}

      {/* Status tabs */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button type="button" key={t.key} style={tabBtnStyle(t.key)} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {/* Search / filter / sort bar */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="search"
          className="form-input"
          placeholder="Search genus or species…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{ flex: '1 1 140px', fontSize: '0.78rem', padding: '0.25rem 0.4rem' }}
        />
        <select
          className="form-input"
          value={sourceFilter}
          onChange={e => setSourceFilter(e.target.value)}
          style={{ fontSize: '0.78rem', padding: '0.25rem 0.4rem', flex: '0 0 auto' }}
        >
          <option value="">All sources</option>
          <option value="collection">Collection</option>
          <option value="external">External</option>
        </select>
        <select
          className="form-input"
          value={`${sortBy}:${sortDir}`}
          onChange={e => { const [by, dir] = e.target.value.split(':'); setSortBy(by); setSortDir(dir); }}
          style={{ fontSize: '0.78rem', padding: '0.25rem 0.4rem', flex: '0 0 auto' }}
        >
          <option value="created_at:desc">Newest first</option>
          <option value="created_at:asc">Oldest first</option>
          <option value="genus:asc">Species A–Z</option>
          <option value="genus:desc">Species Z–A</option>
          <option value="photo_part:asc">Part A–Z</option>
          <option value="growth_stage:asc">Stage: New→Mature</option>
        </select>
      </div>

      {loading && <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Loading…</p>}
      {err && <p style={{ fontSize: '0.82rem', color: 'var(--danger)' }}>{err}</p>}
      {!loading && photos && photos.length === 0 && (
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: 0 }}>
          {q || sourceFilter ? 'No photos match your filters.' : `No ${tab} photos.`}
        </p>
      )}
      {lightboxPhoto && (tab === 'pending' || tab === 'approved') && (
        <RefPhotoLightbox
          photo={lightboxPhoto}
          onClose={() => setLightboxPhoto(null)}
          onTransformed={onTransformed}
        />
      )}
      {photos && photos.map(photo => (
        <div key={photo.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.6rem 0', borderBottom: '1px solid var(--border)' }}>
          {(tab === 'pending' || tab === 'approved') ? (
            <button
              type="button"
              className="ref-photo-thumb-btn"
              onClick={() => setLightboxPhoto(photo)}
              title="Open to rotate / crop"
            >
              <img
                src={`/operator/reference-photos/${photo.id}/file${photo.transform_version ? `?v=${photo.transform_version}` : ''}`}
                alt={photo.photo_part || 'Reference'}
                style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, display: 'block' }}
              />
            </button>
          ) : (
            <img
              src={`/operator/reference-photos/${photo.id}/file`}
              alt={photo.photo_part || 'Reference'}
              style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)', flexShrink: 0 }}
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              <em>{photo.genus} {photo.species}</em>
              {' '}
              <span style={{
                fontSize: '0.7rem', fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                background: photo.source_type === 'collection' ? 'var(--blue-bg, #dbeafe)' : 'var(--amber-bg, #fef3c7)',
                color: photo.source_type === 'collection' ? 'var(--blue, #1d4ed8)' : 'var(--amber, #d97706)',
              }}>
                {photo.source_type === 'collection' ? 'Collection' : 'External'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '0.72rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Photo label</label>
              <select
                className="form-input"
                style={{ fontSize: '0.75rem', padding: '0.15rem 0.3rem', height: 'auto' }}
                value={localParts[photo.id] ?? photo.photo_part ?? ''}
                onChange={e => updatePart(photo.id, e.target.value)}
                disabled={busy[`part_${photo.id}`]}
              >
                {PHOTO_PART_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <label style={{ fontSize: '0.72rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Foliage stage</label>
              <select
                className="form-input"
                style={{ fontSize: '0.75rem', padding: '0.15rem 0.3rem', height: 'auto' }}
                value={localStages[photo.id] ?? photo.growth_stage ?? ''}
                onChange={e => updateGrowthStage(photo.id, e.target.value)}
                disabled={busy[`stage_${photo.id}`]}
              >
                {PHOTO_GROWTH_STAGE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {photo.source_type === 'collection'
                ? (photo.submitter_display_name || photo.submitter_email || 'Unknown user')
                : (photo.attribution || 'No attribution')}
              {' · '}{formatDate(photo.created_at?.split('T')[0] || photo.created_at?.split(' ')[0])}
              {tab !== 'pending' && photo.reviewer_display_name && (
                <> · reviewed by {photo.reviewer_display_name}</>
              )}
            </div>
            {photo.source_type === 'external' && photo.source_url && (
              <div style={{ fontSize: '0.72rem', marginTop: 2 }}>
                <a href={photo.source_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                  {photo.source_url.length > 60 ? photo.source_url.slice(0, 60) + '…' : photo.source_url}
                </a>
                {photo.license && (
                  <span style={{ marginLeft: 6, fontSize: '0.68rem', background: 'var(--border)', padding: '1px 4px', borderRadius: 3 }}>{photo.license}</span>
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', flexShrink: 0 }}>
            {tab === 'pending' && (
              <>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => approve(photo.id)} disabled={busy[`approve_${photo.id}`]}>
                  {busy[`approve_${photo.id}`] ? '…' : 'Approve'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" style={{ color: 'var(--danger)' }} onClick={() => reject(photo.id)} disabled={busy[`reject_${photo.id}`]}>
                  {busy[`reject_${photo.id}`] ? '…' : 'Reject'}
                </button>
              </>
            )}
            {tab === 'approved' && (
              <>
                <button type="button" className="btn btn-secondary btn-sm" style={{ color: 'var(--danger)' }} onClick={() => reject(photo.id)} disabled={busy[`reject_${photo.id}`]}>
                  {busy[`reject_${photo.id}`] ? '…' : 'Revoke'}
                </button>
                <ConfirmButton label="Delete" confirmLabel="Permanently delete this reference photo" onConfirm={() => remove(photo.id)} busy={busy[`delete_${photo.id}`]} />
              </>
            )}
            {tab === 'rejected' && (
              <>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => requeue(photo.id)} disabled={busy[`requeue_${photo.id}`]}>
                  {busy[`requeue_${photo.id}`] ? '…' : 'Re-queue'}
                </button>
                <ConfirmButton label="Delete" confirmLabel="Permanently delete this reference photo" onConfirm={() => remove(photo.id)} busy={busy[`delete_${photo.id}`]} />
              </>
            )}
          </div>
        </div>
      ))}
    </Accordion>
  );
}

const TRAIT_GROUP_LABELS = { leaf: 'Leaf', petiole: 'Petiole', stem_node: 'Stem / Node', variegation: 'Variegation' };

function _initTraitValues(schemaFields, traits) {
  const flat = {};
  for (const field of schemaFields) {
    const dotIdx = field.path.indexOf('.');
    const group = field.path.slice(0, dotIdx);
    const fieldKey = field.path.slice(dotIdx + 1);
    const groupData = (traits && traits[group]) || {};
    if (field.type === 'stage_pair') {
      // Normalize sub-values: coerce strings to single-element arrays
      const raw = groupData[fieldKey] || {};
      const toArr = v => Array.isArray(v) ? [...v] : (v && typeof v === 'string' ? [v] : []);
      flat[field.path] = { juvenile: toArr(raw.juvenile), mature: toArr(raw.mature) };
    } else if (field.type === 'boolean') {
      const v = groupData[fieldKey];
      flat[field.path] = v === true ? true : v === false ? false : null;
    } else if (field.type === 'array') {
      // Coerce bare strings to single-element arrays (pre-normalization format)
      const raw = groupData[fieldKey];
      flat[field.path] = Array.isArray(raw) ? [...raw] : (raw && typeof raw === 'string' ? [raw] : []);
    } else {
      flat[field.path] = groupData[fieldKey] || null;
    }
  }
  return flat;
}

function _buildTraitsObject(schemaFields, values, reviewFlags) {
  const traits = {};
  for (const field of schemaFields) {
    const dotIdx = field.path.indexOf('.');
    const group = field.path.slice(0, dotIdx);
    const fieldKey = field.path.slice(dotIdx + 1);
    if (!traits[group]) traits[group] = {};
    const val = values[field.path];
    if (field.type === 'boolean') {
      if (val !== null && val !== undefined) traits[group][fieldKey] = val;
    } else if (field.type === 'array') {
      if (Array.isArray(val) && val.length > 0) traits[group][fieldKey] = val;
    } else if (field.type === 'stage_pair') {
      if (val && ((val.juvenile && val.juvenile.length) || (val.mature && val.mature.length))) {
        traits[group][fieldKey] = val;
      }
    } else if (val) {
      traits[group][fieldKey] = val;
    }
  }
  for (const g of Object.keys(traits)) {
    if (Object.keys(traits[g]).length === 0) delete traits[g];
  }
  if (Object.keys(reviewFlags).length > 0) traits._review_flags = { ...reviewFlags };
  return traits;
}

function ProfileTraitsEditor({ profileId, initialTraits, initialUpdatedAt }) {
  const [schema, setSchema] = useState(null);
  const [schemaError, setSchemaError] = useState(null);
  const [values, setValues] = useState({});
  const [reviewFlags, setReviewFlags] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [traitsUpdatedAt, setTraitsUpdatedAt] = useState(initialUpdatedAt || null);

  useEffect(() => {
    let active = true;
    apiJson('/id-traits/schema')
      .then(data => { if (active) setSchema(data.fields); })
      .catch(e => { if (active) setSchemaError(e.message); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!schema) return;
    setValues(_initTraitValues(schema, initialTraits));
    setReviewFlags((initialTraits && initialTraits._review_flags) || {});
  }, [schema]); // eslint-disable-line react-hooks/exhaustive-deps

  function clearFlag(path) {
    setReviewFlags(prev => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
  }

  function toggleArrayValue(path, v) {
    setValues(prev => {
      const cur = prev[path] || [];
      return {
        ...prev,
        [path]: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v],
      };
    });
    if (reviewFlags[path]) clearFlag(path);
  }

  function setSingleValue(path, v) {
    setValues(prev => ({ ...prev, [path]: prev[path] === v ? null : v }));
    if (reviewFlags[path]) clearFlag(path);
  }

  function setBoolValue(path, v) {
    setValues(prev => ({ ...prev, [path]: prev[path] === v ? null : v }));
    if (reviewFlags[path]) clearFlag(path);
  }

  function toggleStageValue(path, stage, v) {
    setValues(prev => {
      const cur = prev[path] || { juvenile: [], mature: [] };
      const stageArr = cur[stage] || [];
      return {
        ...prev,
        [path]: {
          ...cur,
          [stage]: stageArr.includes(v) ? stageArr.filter(x => x !== v) : [...stageArr, v],
        },
      };
    });
    if (reviewFlags[path]) clearFlag(path);
  }

  async function handleSave() {
    if (!schema) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    setFieldErrors({});
    try {
      const traitsObj = _buildTraitsObject(schema, values, reviewFlags);
      const res = await apiFetch(`/operator/care-profiles/${profileId}/traits`, {
        method: 'PATCH',
        body: JSON.stringify({ identification_traits: traitsObj }),
      });

      if (res.status === 400) {
        let data;
        try { data = await res.json(); } catch { data = null; }
        const errors = data?.detail?.errors;
        if (Array.isArray(errors)) {
          const byField = {};
          for (const msg of errors) {
            const m = msg.match(/^([^:]+):/);
            const key = m ? m[1].trim() : '__general__';
            byField[key] = (byField[key] || []).concat(msg);
          }
          setFieldErrors(byField);
          setSaveError('Some fields have invalid values — see below.');
        } else {
          setSaveError(data?.detail ? String(data.detail) : 'Validation failed (400)');
        }
        return;
      }

      if (!res.ok) {
        const errMsg = await readApiError(res);
        setSaveError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
        return;
      }

      const result = await res.json();
      if (result.ok) {
        setSaveSuccess(true);
        if (result.traits_updated_at) setTraitsUpdatedAt(result.traits_updated_at);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (e) {
      setSaveError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (schemaError) {
    return <p style={{ fontSize: '0.82rem', color: 'var(--danger)', margin: '0.5rem 0' }}>Failed to load trait schema: {schemaError}</p>;
  }
  if (!schema) {
    return <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '0.5rem 0' }}>Loading trait schema…</p>;
  }

  // Group fields
  const groups = {};
  for (const field of schema) {
    const group = field.path.split('.')[0];
    if (!groups[group]) groups[group] = [];
    groups[group].push(field);
  }

  return (
    <div style={{ marginTop: '1.5rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
      <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.75rem', color: 'var(--text)' }}>
        Identification Traits
        {traitsUpdatedAt && (
          <span style={{ fontWeight: 400, fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
            Last updated: {traitsUpdatedAt.slice(0, 10)}
          </span>
        )}
      </div>

      {Object.entries(groups).map(([group, fields]) => (
        <div key={group} style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
            {TRAIT_GROUP_LABELS[group] || group}
          </div>

          {fields.map(field => {
            const isFlagged = !!reviewFlags[field.path];
            const fieldErr = fieldErrors[field.path];

            return (
              <div key={field.path} style={{ marginBottom: '0.85rem' }}>
                <div style={{ fontSize: '0.78rem', color: 'var(--text)', marginBottom: isFlagged ? '0.15rem' : '0.3rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  {field.label}
                  {isFlagged && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--warning)', fontWeight: 600 }}>⚑</span>
                  )}
                  {field.is_cultivar_discriminator && (
                    <span title="Key cultivar discriminator" style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>★</span>
                  )}
                </div>
                {isFlagged && reviewFlags[field.path] && (
                  <p style={{ fontSize: '0.72rem', color: 'var(--warning)', margin: '0 0 0.4rem 0', lineHeight: 1.4 }}>
                    {reviewFlags[field.path]}
                  </p>
                )}

                {field.type === 'array' && (
                  <div className="health-chip-row">
                    {field.valid_values.map(v => {
                      const active = (values[field.path] || []).includes(v);
                      return (
                        <button
                          key={v}
                          type="button"
                          className={`health-chip ${active ? 'health-chip--active' : ''}`}
                          style={isFlagged && !active ? { borderColor: 'var(--warning-border)', borderWidth: '2px' } : undefined}
                          onClick={() => toggleArrayValue(field.path, v)}
                        >
                          {v.replace(/_/g, ' ')}
                        </button>
                      );
                    })}
                  </div>
                )}

                {field.type === 'string' && (
                  <div className="health-chip-row">
                    {field.valid_values.map(v => {
                      const active = values[field.path] === v;
                      return (
                        <button
                          key={v}
                          type="button"
                          className={`health-chip ${active ? 'health-chip--active' : ''}`}
                          style={isFlagged && !active ? { borderColor: 'var(--warning-border)', borderWidth: '2px' } : undefined}
                          onClick={() => setSingleValue(field.path, v)}
                        >
                          {v.replace(/_/g, ' ')}
                        </button>
                      );
                    })}
                  </div>
                )}

                {field.type === 'boolean' && (
                  <div className="health-chip-row">
                    {[['present', true], ['absent', false]].map(([label, bv]) => {
                      const active = values[field.path] === bv;
                      return (
                        <button
                          key={label}
                          type="button"
                          className={`health-chip ${active ? 'health-chip--active' : ''}`}
                          style={isFlagged && !active ? { borderColor: 'var(--warning-border)', borderWidth: '2px' } : undefined}
                          onClick={() => setBoolValue(field.path, bv)}
                        >
                          {label.charAt(0).toUpperCase() + label.slice(1)}
                        </button>
                      );
                    })}
                  </div>
                )}

                {field.type === 'stage_pair' && (
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {field.stage_keys.map(stage => (
                      <div key={stage} style={{ flex: '1', minWidth: '180px' }}>
                        <div style={{ fontSize: '0.71rem', color: 'var(--text-muted)', marginBottom: '0.2rem', fontWeight: 600 }}>
                          {stage.charAt(0).toUpperCase() + stage.slice(1)}
                        </div>
                        <div className="health-chip-row">
                          {field.valid_values.map(v => {
                            const stageVals = (values[field.path] || {})[stage] || [];
                            const active = stageVals.includes(v);
                            return (
                              <button
                                key={v}
                                type="button"
                                className={`health-chip ${active ? 'health-chip--active' : ''}`}
                                onClick={() => toggleStageValue(field.path, stage, v)}
                              >
                                {v.replace(/_/g, ' ')}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {fieldErr && (
                  <p style={{ fontSize: '0.75rem', color: 'var(--danger)', margin: '0.2rem 0 0' }}>
                    {Array.isArray(fieldErr) ? fieldErr.join('; ') : fieldErr}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {saveError && (
        <p style={{ fontSize: '0.82rem', color: 'var(--danger)', margin: '0.25rem 0' }}>{saveError}</p>
      )}
      {saveSuccess && (
        <p style={{ fontSize: '0.82rem', color: 'var(--green)', margin: '0.25rem 0' }}>Traits saved.</p>
      )}

      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={handleSave}
        disabled={saving || !schema}
        style={{ marginTop: '0.5rem' }}
      >
        {saving ? 'Saving…' : 'Save traits'}
      </button>
    </div>
  );
}

function CareProfileRow({ profile, expanded, restoreScrollY, onExpand, onCollapse, onAction }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [form, setForm] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [rawText, setRawText] = useState('');
  const [rawError, setRawError] = useState(null);
  const [schemaWarning, setSchemaWarning] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [publicMessage, setPublicMessage] = useState('');
  const [busy, setBusy] = useState({});
  const [err, setErr] = useState(null);

  const openDetail = async () => {
    onExpand(profile.id, window.scrollY);
    setErr(null);
    if (detail) return;
    setLoadingDetail(true);
    try {
      const d = await apiJson(`/operator/care-profiles/${profile.id}`);
      setDetail(d);
      if (d.profile_json) {
        setForm(profileToForm(d.profile_json));
        // Detect fields the guided form preserves but cannot directly edit.
        const extra = Object.keys(d.profile_json).filter(k => !GUIDED_PROFILE_KEYS.has(k));
        setSchemaWarning(extra.length > 0 ? `Fields not shown in guided editor: ${extra.join(', ')}` : null);
      }
      setAdminNotes(d.admin_notes || '');
      setPublicMessage(d.public_message || '');
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoadingDetail(false);
    }
  };

  const toggleRaw = () => {
    if (!showRaw) {
      // entering raw mode: serialize current form state
      setRawText(JSON.stringify(formToProfile(form), null, 2));
      setRawError(null);
    } else {
      // leaving raw mode: try to parse back into form
      try {
        const parsed = JSON.parse(rawText);
        setForm(profileToForm(parsed));
        const extra = Object.keys(parsed).filter(k => !GUIDED_PROFILE_KEYS.has(k));
        setSchemaWarning(extra.length > 0 ? `Fields not shown in guided editor: ${extra.join(', ')}` : null);
        setRawError(null);
      } catch (e) {
        setRawError(e.message);
        return; // stay in raw mode if parse fails
      }
    }
    setShowRaw(s => !s);
  };

  const act = async (key, fn) => {
    setBusy(b => ({ ...b, [key]: true }));
    setErr(null);
    try {
      await fn();
      onCollapse();
      setDetail(null);
      setForm(null);
      setShowRaw(false);
      await onAction({ restoreScrollY });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  };

  const handleApprove = () => {
    if (showRaw) {
      try { JSON.parse(rawText); }
      catch (e) { setErr(`Invalid JSON: ${e.message}`); return; }
      act('approve', () => apiJson(`/operator/care-profiles/${profile.id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ admin_notes: adminNotes || null, profile_json: rawText }),
      }));
    } else {
      if (!form) return;
      act('approve', () => apiJson(`/operator/care-profiles/${profile.id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ admin_notes: adminNotes || null, profile_json: JSON.stringify(formToProfile(form)) }),
      }));
    }
  };

  const handleReject = () => {
    if (!adminNotes.trim()) { setErr('Admin notes are required to reject.'); return; }
    act('reject', () => apiJson(`/operator/care-profiles/${profile.id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ admin_notes: adminNotes, public_message: publicMessage.trim() || null }),
    }));
  };

  const handleRequeue = () => {
    act('requeue', () => apiJson(`/operator/care-profiles/${profile.id}/requeue`, { method: 'POST' }));
  };

  const handleMarkStale = () => {
    act('stale', () => apiJson(`/operator/care-profiles/${profile.id}/mark-stale`, { method: 'POST' }));
  };

  const listVerdict = profile.review_verdict;
  const listPriority = profile.review_priority;
  const listConfidence = profile.review_confidence;

  return (
    <div style={{ padding: '0.6rem 0', borderBottom: '1px solid var(--border)' }}>
      {/* Summary row */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.2rem' }}>
        <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>
          <em>{profile.genus} {profile.species}</em>
        </span>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
          {profile.status}
        </span>
        {profile.profile_type && (
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
            {profile.profile_type.replace(/_/g, ' ')}
          </span>
        )}
        {profileNeedsTraitReview(profile) && (
          <span style={{ fontSize: '0.72rem', color: 'var(--orange, #e07b3c)', fontWeight: 600 }}>● review</span>
        )}
        {listVerdict && (
          <span style={{ fontSize: '0.76rem', fontWeight: 600, ...(VERDICT_STYLE[listVerdict] || {}) }}>
            {listVerdict.replace(/_/g, ' ')}
          </span>
        )}
        {listPriority && <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{listPriority}</span>}
        {listConfidence && <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>conf: {listConfidence}</span>}
      </div>
      <p style={{ margin: '0 0 0.35rem', fontSize: '0.76rem', color: 'var(--text-muted)' }}>
        {profile.generation_model || '—'} &middot; {profile.generated_at ? profile.generated_at.split('T')[0] : 'no date'}
        {profile.schema_version && ` · schema ${profile.schema_version}`}
      </p>
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
        {!expanded
          ? <button type="button" className="settings-lookup-action" onClick={openDetail}>Review</button>
          : <button type="button" className="settings-lookup-action" onClick={onCollapse}>Collapse</button>
        }
        {profile.status !== 'stale' && profile.status !== 'approved' && (
          <ConfirmButton label="Mark Stale" confirmLabel="Mark stale for regeneration" onConfirm={handleMarkStale} busy={busy.stale} danger={false} />
        )}
        {(profile.status === 'stale' || profile.status === 'rejected') && !expanded && (
          <ConfirmButton label="Re-queue" confirmLabel="Add back to generation queue?" onConfirm={handleRequeue} busy={busy.requeue} danger={false} />
        )}
      </div>

      {/* Detail panel */}
      {expanded && (
        <div style={{ marginTop: '0.75rem' }}>
          {loadingDetail && <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Loading…</p>}

          {/* ── Care Profile ───────────────────────────────────────────── */}
          <Accordion title="Care Profile" defaultOpen={false}>
            {/* Schema warning */}
            {!loadingDetail && schemaWarning && (
              <div style={{ marginBottom: '0.6rem', padding: '0.4rem 0.65rem', background: '#fffae0', border: '1px solid #f0d899', borderRadius: 6, fontSize: '0.78rem', color: '#7a6200' }}>
                Warning: {schemaWarning}. The guided editor will preserve these fields, but use raw JSON view to edit them.
              </div>
            )}

            {/* View toggle */}
            {!loadingDetail && form && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
                <button type="button" className="settings-lookup-action" onClick={toggleRaw} style={{ fontSize: '0.76rem' }}>
                  {showRaw ? 'Guided editor' : 'Raw JSON'}
                </button>
                {showRaw && <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Editing raw JSON — click "Guided editor" to sync back to form</span>}
              </div>
            )}

            {/* Guided section editor */}
            {!loadingDetail && form && !showRaw && (
              <GuidedEditor form={form} setForm={setForm} review={detail?.review_json} />
            )}

            {/* Raw JSON editor */}
            {!loadingDetail && form && showRaw && (
              <div style={{ marginBottom: '0.5rem' }}>
                <textarea
                  value={rawText}
                  onChange={e => { setRawText(e.target.value); setRawError(null); }}
                  className="form-input form-textarea"
                  rows={20}
                  style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                />
                {rawError && <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: 'var(--danger)' }}>Invalid JSON: {rawError}</p>}
              </div>
            )}

            {/* Admin notes */}
            {!loadingDetail && form && (
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>
                  Admin Notes{profile.status !== 'approved' ? ' (required to reject)' : ''}
                </label>
                <textarea
                  value={adminNotes}
                  onChange={e => setAdminNotes(e.target.value)}
                  className="form-input form-textarea"
                  rows={2}
                  placeholder="Notes on edits made, rejection reason, etc."
                />
              </div>
            )}

            {/* Public rejection message */}
            {!loadingDetail && form && (
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>
                  Public Message <span style={{ fontWeight: 400 }}>(shown on plant page if rejected — optional)</span>
                </label>
                <textarea
                  value={publicMessage}
                  onChange={e => setPublicMessage(e.target.value)}
                  className="form-input form-textarea"
                  rows={2}
                  placeholder="e.g. This cultivar is identical to the species in care — use the species profile directly."
                />
              </div>
            )}

            {err && <p style={{ margin: '0.25rem 0', fontSize: '0.82rem', color: 'var(--danger)' }}>{err}</p>}

            {/* Action buttons */}
            {!loadingDetail && form && (
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" className="btn btn-primary btn-sm" onClick={handleApprove} disabled={busy.approve}>
                  {busy.approve ? 'Approving…' : 'Approve'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleReject} disabled={busy.reject} style={{ color: 'var(--danger)' }}>
                  {busy.reject ? 'Rejecting…' : 'Reject'}
                </button>
                <ConfirmButton label="Mark Stale" confirmLabel="Mark stale for regeneration" onConfirm={handleMarkStale} busy={busy.stale} danger={false} />
                {(profile.status === 'stale' || profile.status === 'rejected') && (
                  <ConfirmButton label="Re-queue" confirmLabel="Add back to generation queue?" onConfirm={handleRequeue} busy={busy.requeue} danger={false} />
                )}
                <button type="button" className="settings-lookup-action" onClick={onCollapse}>Cancel</button>
              </div>
            )}
          </Accordion>

          {/* ── Reference Photos ───────────────────────────────────────── */}
          <Accordion title="Reference Photos" defaultOpen={false} style={{ marginTop: '0.5rem' }}>
            <ProfileReferencePhotoManager profileId={profile.id} genus={profile.genus} species={profile.species} />
          </Accordion>

          {/* ── Identification Traits ──────────────────────────────────── */}
          {(() => {
            const traitFlags = detail?.identification_traits?._review_flags || {};
            const flagCount = Object.values(traitFlags).filter(Boolean).length;
            return (
              <Accordion
                title="Identification Traits"
                defaultOpen={flagCount > 0}
                badge={flagCount > 0 ? `${flagCount} flag${flagCount !== 1 ? 's' : ''}` : undefined}
                style={{ marginTop: '0.5rem' }}
              >
                <ProfileTraitsEditor
                  profileId={profile.id}
                  initialTraits={detail ? detail.identification_traits : null}
                  initialUpdatedAt={detail ? detail.traits_updated_at : null}
                />
              </Accordion>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function CareProfilesSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = CARE_PROFILE_STATUS_TABS.includes(searchParams.get('status')) ? searchParams.get('status') : 'draft';
  const initialSort = SORT_PARAM_MAP[searchParams.get('sort')] ? searchParams.get('sort') : 'created_desc';
  const initialPreset = CARE_PROFILE_PRESETS.some(p => p.key === searchParams.get('preset')) ? searchParams.get('preset') : 'all';
  const initialProfileId = parseInt(searchParams.get('profile'), 10) || null;
  const [activeTab, setActiveTab] = useState(initialStatus);
  const [profileQuery, setProfileQuery] = useState(searchParams.get('q') || '');
  const [debouncedProfileQuery, setDebouncedProfileQuery] = useState(searchParams.get('q') || '');
  const [expandedProfileId, setExpandedProfileId] = useState(initialProfileId);
  const [expandedProfileScrollY, setExpandedProfileScrollY] = useState(null);
  const [profilePreset, setProfilePreset] = useState(initialPreset);
  const [profileSort, setProfileSort] = useState(initialSort);
  const [profiles, setProfiles] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [jobs, setJobs] = useState([]);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [jobFilterStatus, setJobFilterStatus] = useState('requested');
  const [jobQuery, setJobQuery] = useState('');
  const [jobActionNote, setJobActionNote] = useState('');
  const [jobActionBusy, setJobActionBusy] = useState('');
  const [jobActionError, setJobActionError] = useState(null);
  const [manualRequest, setManualRequest] = useState({
    profile_type: 'species',
    genus: '',
    species: '',
    species_unknown: false,
    trade_name: '',
    family: '',
    common_name: '',
  });
  const [manualBusy, setManualBusy] = useState(false);
  const [manualError, setManualError] = useState(null);
  const [manualSuccess, setManualSuccess] = useState(null);
  const [batchRefreshKey, setBatchRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTraitReviewOnly, setShowTraitReviewOnly] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedProfileQuery(profileQuery.trim()), 250);
    return () => clearTimeout(t);
  }, [profileQuery]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (activeTab !== 'draft') params.set('status', activeTab);
    if (debouncedProfileQuery) params.set('q', debouncedProfileQuery);
    if (profileSort !== 'created_desc') params.set('sort', profileSort);
    if (profilePreset !== 'all') params.set('preset', profilePreset);
    if (expandedProfileId) params.set('profile', String(expandedProfileId));
    setSearchParams(params, { replace: true });
  }, [activeTab, debouncedProfileQuery, profileSort, profilePreset, expandedProfileId, setSearchParams]);

  const load = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const sortConfig = SORT_PARAM_MAP[profileSort] || SORT_PARAM_MAP.created_desc;
      const profileParams = new URLSearchParams({
        status: activeTab,
        sort_by: sortConfig.sort_by,
        sort_dir: sortConfig.sort_dir,
      });
      if (debouncedProfileQuery) profileParams.set('q', debouncedProfileQuery);
      if (profilePreset !== 'all') profileParams.set('preset', profilePreset);
      const [profileData, jobData] = await Promise.all([
        apiJson(`/operator/care-profiles?${profileParams}`),
        apiJson('/operator/care-generation-jobs'),
      ]);
      setProfiles(profileData.profiles);
      setStatusCounts(profileData.status_counts || {});
      setJobs(jobData);
      setSelectedJobIds(prev => prev.filter(id => jobData.some(job => job.id === id)));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      if (Number.isFinite(options.restoreScrollY)) {
        requestAnimationFrame(() => {
          window.scrollTo({ top: options.restoreScrollY, behavior: 'auto' });
        });
      }
    }
  }, [activeTab, debouncedProfileQuery, profileSort, profilePreset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setExpandedProfileId(null);
    setExpandedProfileScrollY(null);
  }, [activeTab, debouncedProfileQuery]);

  useEffect(() => {
    if (expandedProfileId && profiles.length > 0 && !profiles.some(p => p.id === expandedProfileId)) {
      setExpandedProfileId(null);
      setExpandedProfileScrollY(null);
    }
  }, [profiles, expandedProfileId]);

  const expandProfile = (profileId, scrollY) => {
    setExpandedProfileId(profileId);
    setExpandedProfileScrollY(scrollY);
  };

  const collapseProfile = () => {
    setExpandedProfileId(null);
    setExpandedProfileScrollY(null);
  };

  const visibleJobs = jobs.filter(job => {
    if (jobFilterStatus !== 'all' && job.status !== jobFilterStatus) return false;
    if (!jobQuery.trim()) return true;
    const haystack = `${job.genus} ${job.species} ${job.requested_by_email || ''}`.toLowerCase();
    return haystack.includes(jobQuery.trim().toLowerCase());
  });
  const selectedJobs = jobs.filter(job => selectedJobIds.includes(job.id));
  const selectedRequestedJobIds = selectedJobs.filter(job => job.status === 'requested').map(job => job.id);
  const canRejectSelected = selectedJobs.length > 0 && selectedJobs.every(job => job.status === 'requested');
  const canReturnSelected = selectedJobs.length > 0 && selectedJobs.every(job => ['queued', 'rejected'].includes(job.status));
  const identityField = manualIdentityFieldConfig(manualRequest.profile_type);

  const traitReviewNeededCount = profiles.filter(profileNeedsTraitReview).length;
  const visibleProfiles = showTraitReviewOnly
    ? profiles.filter(profileNeedsTraitReview)
    : profiles;

  const toggleJob = (jobId) => {
    setSelectedJobIds(ids => ids.includes(jobId) ? ids.filter(id => id !== jobId) : [...ids, jobId]);
  };

  const toggleVisibleJobs = () => {
    const visibleIds = visibleJobs.map(job => job.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedJobIds.includes(id));
    if (allVisibleSelected) {
      setSelectedJobIds(ids => ids.filter(id => !visibleIds.includes(id)));
      return;
    }
    setSelectedJobIds(ids => Array.from(new Set([...ids, ...visibleIds])));
  };

  const runJobAction = async (action) => {
    setJobActionBusy(action);
    setJobActionError(null);
    try {
      if (action === 'run') {
        await apiJson('/operator/run-generation', {
          method: 'POST',
          body: JSON.stringify({ command: 'generate', source: 'jobs', job_ids: selectedRequestedJobIds }),
        });
        setBatchRefreshKey(k => k + 1);
      } else {
        await apiJson('/operator/care-generation-jobs/bulk', {
          method: 'POST',
          body: JSON.stringify({
            action,
            job_ids: selectedJobIds,
            admin_note: jobActionNote.trim() || undefined,
          }),
        });
      }
      setSelectedJobIds([]);
      setJobActionNote('');
      await load();
    } catch (e) {
      setJobActionError(e.message);
    } finally {
      setJobActionBusy('');
    }
  };

  const submitManualRequest = async () => {
    setManualBusy(true);
    setManualError(null);
    setManualSuccess(null);
    try {
      const payload = {
        profile_type: manualRequest.profile_type,
        genus: manualRequest.genus,
        family: manualRequest.family || undefined,
        common_name: manualRequest.common_name || undefined,
      };
      if (manualRequest.profile_type === 'trade_name') {
        payload.trade_name = manualRequest.trade_name;
        payload.species = manualRequest.species;
        payload.species_unknown = manualRequest.species_unknown;
      } else if (manualRequest.profile_type !== 'genus') {
        payload.species = manualRequest.species;
      }
      const result = await apiJson('/operator/care-generation-jobs/manual', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setManualSuccess(`${result.genus} ${result.species} queued (${result.job_action === 'reopened' ? 'reopened existing job' : 'new request'})`);
      setManualRequest({
        profile_type: 'species',
        genus: '',
        species: '',
        species_unknown: false,
        trade_name: '',
        family: '',
        common_name: '',
      });
      await load();
    } catch (e) {
      setManualError(e.message);
    } finally {
      setManualBusy(false);
    }
  };

  const refreshBtn = (
    <button type="button" className="settings-lookup-action" onClick={() => load()} disabled={loading}>
      {loading ? 'Loading…' : 'Refresh'}
    </button>
  );

  return (
    <SectionCard title="Care Profiles" action={refreshBtn}>
      {error && <p className="settings-save-error">{error}</p>}

      <Accordion title="Batch Pipeline" defaultOpen={false}>
        <BatchPanel onSaved={load} refreshKey={batchRefreshKey} selectedJobIds={selectedRequestedJobIds} />
      </Accordion>

      <Accordion title="Manual Request" defaultOpen={false}>
      <div style={{ marginBottom: '0.9rem', padding: '0.7rem 0.85rem', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-recessed)' }}>
        <div style={{ marginBottom: '0.55rem' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text)' }}>Manual care request</div>
          <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
            Queue a one-off request directly from the admin console when it does not already exist on a plant record or in the common list.
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.55rem', alignItems: 'start' }}>
          <div>
            <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Profile type</label>
            <select
              value={manualRequest.profile_type}
              onChange={e => {
                const nextType = e.target.value;
                setManualRequest(req => ({
                  ...req,
                  profile_type: nextType,
                  species: '',
                  species_unknown: nextType === 'trade_name',
                  trade_name: '',
                }));
                setManualError(null);
                setManualSuccess(null);
              }}
              className="form-input"
              style={{ fontSize: '0.8rem' }}
            >
              {MANUAL_PROFILE_TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <div style={{ marginTop: '0.2rem', fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
              {MANUAL_PROFILE_TYPE_OPTIONS.find(option => option.value === manualRequest.profile_type)?.detail}
            </div>
          </div>

          <div>
            <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Genus</label>
            <AutocompleteInput
              field="genus"
              value={manualRequest.genus}
              onChange={value => setManualRequest(req => ({ ...req, genus: value }))}
              onSelect={suggestion => setManualRequest(req => ({
                ...req,
                genus: suggestion.value,
                species: '',
                family: req.family || suggestion.family || '',
              }))}
              placeholder={manualRequest.profile_type === 'hybrid' ? 'Philodendron or Alocasia × Colocasia' : 'Philodendron'}
              className="admin-care-autocomplete"
            />
          </div>

          {manualRequest.profile_type === 'trade_name' && (
            <>
              <div>
                <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Species basis</label>
                <AutocompleteInput
                  field="species"
                  value={manualRequest.species}
                  onChange={value => setManualRequest(req => ({ ...req, species: value }))}
                  onSelect={suggestion => setManualRequest(req => ({
                    ...req,
                    species: suggestion.value,
                    species_unknown: false,
                    family: req.family || suggestion.family || '',
                    common_name: req.common_name || suggestion.common_name || '',
                  }))}
                  genusValue={manualRequest.genus}
                  placeholder="micholitziana"
                  className="admin-care-autocomplete"
                  disabled={!manualRequest.genus.trim() || manualRequest.species_unknown}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.35rem', fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                  <input
                    type="checkbox"
                    checked={manualRequest.species_unknown}
                    onChange={e => setManualRequest(req => ({
                      ...req,
                      species_unknown: e.target.checked,
                      species: e.target.checked ? '' : req.species,
                    }))}
                  />
                  <span>Species missing / unknown (use sp.)</span>
                </label>
                <div style={{ marginTop: '0.2rem', fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                  {manualRequest.species_unknown ? 'Queues the identity as sp. plus the trade name.' : 'Use the known botanical species epithet only.'}
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Trade name</label>
                <input
                  value={manualRequest.trade_name}
                  onChange={e => setManualRequest(req => ({ ...req, trade_name: e.target.value }))}
                  placeholder="Florida Green"
                  className="form-input"
                  style={{ fontSize: '0.8rem' }}
                />
                <div style={{ marginTop: '0.2rem', fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                  {identityField.helper}
                </div>
              </div>
            </>
          )}

          {manualRequest.profile_type !== 'trade_name' && identityField.key && (
            <div>
              <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>{identityField.label}</label>
              <AutocompleteInput
                field="species"
                value={manualRequest[identityField.key]}
                onChange={value => setManualRequest(req => ({ ...req, [identityField.key]: value }))}
                onSelect={suggestion => setManualRequest(req => ({
                  ...req,
                  [identityField.key]: suggestion.value,
                  family: req.family || suggestion.family || '',
                  common_name: req.common_name || suggestion.common_name || '',
                }))}
                genusValue={manualRequest.genus}
                placeholder={identityField.placeholder}
                className="admin-care-autocomplete"
                disabled={!manualRequest.genus.trim()}
              />
              <div style={{ marginTop: '0.2rem', fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                {identityField.helper}
              </div>
            </div>
          )}

          {manualRequest.profile_type !== 'trade_name' && !identityField.key && (
            <div style={{ display: 'flex', alignItems: 'center', minHeight: '100%' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
                {identityField.helper}
              </div>
            </div>
          )}

          <div>
            <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Common name</label>
            <input
              value={manualRequest.common_name}
              onChange={e => setManualRequest(req => ({ ...req, common_name: e.target.value }))}
              placeholder="Optional"
              className="form-input"
              style={{ fontSize: '0.8rem' }}
            />
          </div>

          <div>
            <label style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Family</label>
            <input
              value={manualRequest.family}
              onChange={e => setManualRequest(req => ({ ...req, family: e.target.value }))}
              placeholder="Optional"
              className="form-input"
              style={{ fontSize: '0.8rem' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.65rem' }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={submitManualRequest} disabled={manualBusy}>
            {manualBusy ? 'Queueing…' : 'Queue manual request'}
          </button>
          <button
            type="button"
            className="settings-lookup-action"
            onClick={() => {
              setManualRequest({
                profile_type: 'species',
                genus: '',
                species: '',
                species_unknown: false,
                trade_name: '',
                family: '',
                common_name: '',
              });
              setManualError(null);
              setManualSuccess(null);
            }}
            disabled={manualBusy}
          >
            Reset form
          </button>
          {manualSuccess && <span style={{ fontSize: '0.78rem', color: 'var(--green, #3a7d44)' }}>{manualSuccess}</span>}
        </div>
        {manualError && <p className="settings-save-error" style={{ marginTop: '0.45rem' }}>{manualError}</p>}
      </div>
      </Accordion>

      <Accordion title="Generation Queue" defaultOpen badge={`${visibleJobs.length} shown`}>
      <div style={{ marginBottom: '0.9rem', padding: '0.7rem 0.85rem', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-recessed)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.55rem' }}>
          <div>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text)' }}>Generation Queue</div>
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
              Select individual requests to run, reject before generation, or return queued/rejected items to requested.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={jobActionBusy !== '' || selectedRequestedJobIds.length === 0 || selectedRequestedJobIds.length !== selectedJobs.length}
              onClick={() => runJobAction('run')}
            >
              {jobActionBusy === 'run' ? 'Starting…' : `Run selected${selectedRequestedJobIds.length ? ` (${selectedRequestedJobIds.length})` : ''}`}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={jobActionBusy !== '' || !canRejectSelected}
              style={{ color: 'var(--danger)' }}
              onClick={() => runJobAction('reject')}
            >
              {jobActionBusy === 'reject' ? 'Rejecting…' : 'Reject selected'}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={jobActionBusy !== '' || !canReturnSelected}
              onClick={() => runJobAction('requested')}
            >
              {jobActionBusy === 'requested' ? 'Returning…' : 'Return to requested'}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.55rem' }}>
          <select value={jobFilterStatus} onChange={e => setJobFilterStatus(e.target.value)} className="form-input" style={{ width: 140, fontSize: '0.8rem' }}>
            <option value="all">All statuses</option>
            <option value="requested">Requested</option>
            <option value="queued">Queued</option>
            <option value="rejected">Rejected</option>
            <option value="completed">Completed</option>
          </select>
          <input
            value={jobQuery}
            onChange={e => setJobQuery(e.target.value)}
            placeholder="Search genus, species, requester"
            className="form-input"
            style={{ minWidth: 220, flex: '1 1 240px', fontSize: '0.8rem' }}
          />
          <input
            value={jobActionNote}
            onChange={e => setJobActionNote(e.target.value)}
            placeholder="Admin note for reject (optional)"
            className="form-input"
            style={{ minWidth: 220, flex: '1 1 240px', fontSize: '0.8rem' }}
          />
        </div>

        <div style={{ marginBottom: '0.55rem', fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
          Run selected only runs the checked rows here. Use the batch pipeline above when you want to launch all requested jobs at once.
        </div>

        {jobActionError && <p className="settings-save-error" style={{ marginBottom: '0.5rem' }}>{jobActionError}</p>}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.45rem', fontSize: '0.76rem', color: 'var(--text-muted)' }}>
          <span>{visibleJobs.length} queue item{visibleJobs.length !== 1 ? 's' : ''} shown</span>
          <button type="button" className="settings-lookup-action" onClick={toggleVisibleJobs} disabled={visibleJobs.length === 0}>
            {visibleJobs.length > 0 && visibleJobs.every(job => selectedJobIds.includes(job.id)) ? 'Clear visible' : 'Select visible'}
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', maxHeight: 320, overflow: 'auto', paddingRight: '0.1rem' }}>
          {visibleJobs.length === 0 && (
            <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)' }}>No generation jobs match this filter.</p>
          )}
          {visibleJobs.map(job => {
            const selected = selectedJobIds.includes(job.id);
            const profileType = job.profile_type || inferQueueProfileType(job.species);
            return (
              <label
                key={job.id}
                style={{
                  display: 'flex',
                  gap: '0.65rem',
                  alignItems: 'flex-start',
                  padding: '0.55rem 0.65rem',
                  borderRadius: 8,
                  border: selected ? '1px solid var(--green, #3a7d44)' : '1px solid var(--border)',
                  background: selected ? 'var(--green-light, #f0f7f1)' : 'var(--surface)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleJob(job.id)}
                  style={{ marginTop: '0.15rem' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.15rem' }}>
                    <span style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--text)' }}>
                      {job.genus} {job.species}
                    </span>
                    <QueueBadge label={job.status} style={JOB_STATUS_STYLE[job.status] || JOB_STATUS_STYLE.requested} />
                    <QueueBadge label={profileType.replace(/_/g, ' ')} style={{ background: 'var(--surface-recessed)', color: 'var(--text-muted)', border: '1px solid var(--border)' }} />
                  </div>
                  <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    Job #{job.id}
                    {job.requested_by_email ? ` · requested by ${job.requested_by_email}` : ''}
                    {job.requested_at ? ` · ${job.requested_at.split('T')[0]}` : ''}
                    {job.completed_at ? ` · completed ${job.completed_at.split('T')[0]}` : ''}
                  </div>
                  {(job.trade_name || job.common_name) && (
                    <div style={{ marginTop: '0.2rem', fontSize: '0.77rem', color: 'var(--text-muted)', lineHeight: 1.45 }}>
                      {job.trade_name ? `Trade name: ${job.trade_name}` : ''}
                      {job.trade_name && job.common_name ? ' · ' : ''}
                      {job.common_name ? `Common name: ${job.common_name}` : ''}
                    </div>
                  )}
                  {job.error_message && (
                    <div style={{ marginTop: '0.2rem', fontSize: '0.77rem', color: job.status === 'rejected' ? 'var(--danger)' : 'var(--text-muted)' }}>
                      {job.status === 'rejected' ? 'Rejection note' : 'Note'}: {job.error_message}
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>
      </Accordion>

      {/* Status tabs */}
      <div style={{ marginBottom: '0.75rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.6rem' }}>
        <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.55rem', flexWrap: 'wrap' }}>
          {CARE_PROFILE_STATUS_TABS.map(tab => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '0.3rem 0.7rem',
                fontSize: '0.8rem',
                fontWeight: activeTab === tab ? 700 : 400,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                background: activeTab === tab ? 'var(--green, #3a7d44)' : 'transparent',
                color: activeTab === tab ? '#fff' : 'var(--text-muted)',
                textTransform: 'capitalize',
              }}
            >
              {tab} {statusCounts[tab] != null ? `(${statusCounts[tab]})` : ''}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.45rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            value={profileQuery}
            onChange={e => setProfileQuery(e.target.value)}
            placeholder="Search profiles by genus, species, common name, notes…"
            className="form-input"
            style={{ minWidth: 240, flex: '1 1 280px', fontSize: '0.82rem' }}
          />
          <select
            value={profileSort}
            onChange={e => setProfileSort(e.target.value)}
            className="form-input"
            style={{ width: 180, fontSize: '0.82rem' }}
          >
            {CARE_PROFILE_SORTS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {profileQuery && (
            <button type="button" className="settings-lookup-action" onClick={() => setProfileQuery('')}>
              Clear
            </button>
          )}
          {traitReviewNeededCount > 0 && (
            <span style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--orange, #e07b3c)', whiteSpace: 'nowrap' }}>
              {traitReviewNeededCount} need review
            </span>
          )}
          {traitReviewNeededCount > 0 && (
            <button
              type="button"
              className={`settings-lookup-action${showTraitReviewOnly ? '' : ' settings-lookup-action--ghost'}`}
              onClick={() => setShowTraitReviewOnly(!showTraitReviewOnly)}
              style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}
            >
              {showTraitReviewOnly ? 'Show all' : 'Show only'}
            </button>
          )}
          {!loading && (
            <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
              {visibleProfiles.length} {activeTab} profile{visibleProfiles.length === 1 ? '' : 's'}
              {(debouncedProfileQuery || profilePreset !== 'all' || showTraitReviewOnly) ? ' matched' : ''}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.45rem' }}>
          {CARE_PROFILE_PRESETS.map(preset => (
            <button
              key={preset.key}
              type="button"
              className={`filter-chip${profilePreset === preset.key ? ' filter-chip-active' : ''}`}
              onClick={() => setProfilePreset(preset.key)}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</p>}
      {!loading && visibleProfiles.length === 0 && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          {debouncedProfileQuery || profilePreset !== 'all' ? `No ${activeTab} profiles match these filters.` : `No ${activeTab} profiles.`}
        </p>
      )}
      {visibleProfiles.map(profile => (
        <CareProfileRow
          key={profile.id}
          profile={profile}
          expanded={expandedProfileId === profile.id}
          restoreScrollY={expandedProfileId === profile.id ? expandedProfileScrollY : null}
          onExpand={expandProfile}
          onCollapse={collapseProfile}
          onAction={load}
        />
      ))}
    </SectionCard>
  );
}

export function CareProfilesPage({ session }) {
  const navigate = useNavigate();
  const isAdmin = session?.user?.is_superadmin;
  const connectivity = useConnectivity();

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (connectivity === false) {
    return (
      <div className="app-scroll-area">
        <header className="sub-header">
          <button type="button" className="sub-header-back" onClick={() => navigate('/admin', { replace: true })} aria-label="Back" title="Back">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title"><h1>Care Profiles</h1></div>
        </header>
        <div className="stats-content"><ErrorScreen title="No connection" message="Load failed" /></div>
      </div>
    );
  }
  if (connectivity === null) {
    return (
      <div className="app-scroll-area">
        <header className="sub-header">
          <button type="button" className="sub-header-back" onClick={() => navigate('/admin', { replace: true })} aria-label="Back" title="Back">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title"><h1>Care Profiles</h1></div>
        </header>
        <div className="stats-content" style={{ padding: '2rem 1rem' }}><p className="health-loading">Checking connection…</p></div>
      </div>
    );
  }

  return (
    <div className="app-scroll-area">
      <header className="sub-header">
        <button type="button" className="sub-header-back" onClick={() => navigate('/admin', { replace: true })} aria-label="Back" title="Back">
          <Icon name="arrowLeft" size={20} />
        </button>
        <div className="sub-header-title">
          <h1>Care Profiles</h1>
        </div>
      </header>
      <div className="stats-content" style={{ paddingBottom: '2rem' }}>
        <ReferencePhotoQueueSection />
        <CareProfilesSection />
      </div>
    </div>
  );
}

// ─── Operator Console ──────────────────────────────────────────────────────

export default function OperatorConsole({ session }) {
  const isAdmin = session?.user?.is_superadmin;
  const connectivity = useConnectivity();

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const adminHeader = (
    <header className="app-header">
      <div className="app-header-brand">
        <img src="/logo_only.svg" alt="Aroidex" className="app-header-logo" />
        <div>
          <h1>Operator Console</h1>
          <p className="plant-count">Logged in as {session.user.email}</p>
        </div>
      </div>
    </header>
  );

  if (connectivity === false) {
    return (
      <div className="app-scroll-area">
        {adminHeader}
        <div className="stats-content"><ErrorScreen title="No connection" message="Load failed" /></div>
      </div>
    );
  }
  if (connectivity === null) {
    return (
      <div className="app-scroll-area">
        {adminHeader}
        <div className="stats-content" style={{ padding: '2rem 1rem' }}><p className="health-loading">Checking connection…</p></div>
      </div>
    );
  }

  return (
    <div className="app-scroll-area">
      {adminHeader}
      <div className="stats-content" style={{ paddingBottom: '2rem' }}>

        <OverviewSection />
        <AdminWorkspacesSection />
        <SignupModeSection />
        <IntegrationsSection />
        <UsersSection />
        <HouseholdsSection />
        <LoginAttemptsSection />
        <SessionsSection />
        <AuditSection />
        <InfrastructureSection />
        <MigrationsSection />
      </div>
    </div>
  );
}
